// Checkout branding — typed shape, design tokens, palettes, persistence
// codec, CSS sanitizer, and DOM helpers for `--dp-*` CSS variables.
//
// Both delopay-checkout and delopay-control-center consume this module.
//
// Persistence: top-level fields own the "loud" tokens (logo, theme,
// payment_button_*, background_colour, etc.). Everything else lives in
// `sdk_ui_rules.branding` as a nested string-keyed map — booleans and
// numbers round-trip as strings, the trust-badge list is JSON-stringified.

import {
  AIRWALLEX_AUTOFILL_TREATMENTS,
  AIRWALLEX_CARD_PRESETS,
  AIRWALLEX_FIELD_CHROMES,
  AIRWALLEX_FIELD_FONT_SIZES,
  AIRWALLEX_FIELD_WEIGHTS,
  AIRWALLEX_FOCUS_RINGS,
  AIRWALLEX_LABEL_STYLES,
  type AirwallexAutofillTreatment,
  type AirwallexCardPreset,
  type AirwallexFieldChrome,
  type AirwallexFieldFontSize,
  type AirwallexFieldWeight,
  type AirwallexFocusRing,
  type AirwallexLabelStyle,
} from './airwallexCard';
import {
  APPLE_PAY_BUTTON_STYLES,
  APPLE_PAY_BUTTON_TYPES,
  CARD_FIELD_CHROMES,
  CARD_FIELD_FONT_SIZES,
  CARD_FIELD_WEIGHTS,
  CARD_FOCUS_RINGS,
  GOOGLE_PAY_BUTTON_COLORS,
  GOOGLE_PAY_BUTTON_TYPES,
  NOMUPAY_FORM_STYLES,
  PAYPAL_CARD_LAYOUTS,
  PAYPAL_LABEL_STYLES,
  STRIPE_INPUT_STYLES,
  STRIPE_LABEL_PLACEMENTS,
  STRIPE_SPACINGS,
  STRIPE_THEMES,
  type ApplePayButtonStyle,
  type ApplePayButtonType,
  type CardFieldChrome,
  type CardFieldFontSize,
  type CardFieldWeight,
  type CardFocusRing,
  type GooglePayButtonColor,
  type GooglePayButtonType,
  type NomupayFormStyle,
  type PaypalCardLayout,
  type PaypalLabelStyle,
  type StripeInputStyle,
  type StripeLabelPlacement,
  type StripeSpacing,
  type StripeTheme,
} from './cardFormStyles';

// --- Types --------------------------------------------------------------

export type CornerRadius = 'square' | 'small' | 'medium' | 'large' | 'pill';

// Surfaces and inputs never make sense pill-shaped (a pill input ends up with
// half-circle ends crammed against text). The form picker only exposes these
// four; if a stale 'pill' value lands here from older saved data, decode
// drops it back to 'medium'. Buttons and badges still allow 'pill'.
export type NonPillRadius = Exclude<CornerRadius, 'pill'>;

// Spacing scales — split apart so the merchant can independently tune
// surface padding, vertical rhythm, input height and pay-button height. Used
// to live as a single `density` enum but that conflated four dimensions.
export type SpacingScale = 'compact' | 'comfortable' | 'spacious';
export type SizeScale = 'sm' | 'md' | 'lg';
export type SurfaceStyle = 'flat' | 'outlined' | 'elevated';

export type FontFamily =
  | 'inter'
  | 'system'
  | 'serif'
  | 'mono'
  | 'roboto'
  | 'poppins'
  | 'manrope'
  | 'dm-sans'
  | 'space-grotesk'
  | 'plex-sans'
  | 'work-sans'
  | 'open-sans'
  | 'lora'
  | 'playfair'
  | 'plex-mono'
  | 'jetbrains-mono';

export type FontWeight = 'regular' | 'medium' | 'semibold' | 'bold';
export type LayoutStyle = 'compact' | 'split';
export type SummaryPosition = 'left' | 'right';

// Stripe Elements only supports two label modes ("above" or hidden via the
// .Label-collapse hack); a "floating" label is not a Stripe concept and
// would only render in our own mock — preview was lying about it. Persisted
// `'floating'` is silently decoded to `'above'` for back-compat.
export type LabelStyle = 'above' | 'hidden';

// Stripe Elements layout. Honored inside the StripeCardPane only — other
// connector panes stack vertically regardless of this setting.
export type PaymentLayout = 'tabs' | 'accordion' | 'spaced_accordion';

export type LogoShape = 'square' | 'rounded' | 'circle';
export type LogoSize = 'sm' | 'md' | 'lg';

// --- Provider surcharge / discount disclosure ---------------------------
//
// With surcharges scoped to the provider rather than to the payment method,
// two tiles on one checkout can cost the buyer different amounts — so the
// figure has to be legible on the tile, before the choice, not only in the
// breakdown after it. These control how that figure is painted; whether there
// is one at all is the router's answer, per pane.
//
// Nothing here can invent a figure or change one. A merchant who would rather
// not disclose per-tile turns it off with `showSurcharge` and keeps the
// breakdown inside the pane, which is where the disclosure lived before.

// Where the figure sits relative to the tile's label.
//
// `inline` follows the label text immediately, so the price reads as part of
// the name — good for short labels.
// `trailing` pushes it to the tile's trailing edge on the label's own line: the
// price reads as a property of the tile, and the tile's height does not change,
// which matters because most tiles carry no figure at all and a row that grows
// only for some of them makes the list look ragged.
// `below` puts it under the sublabel, for merchants whose labels are long
// enough that a trailing figure would crowd them.
// `above` puts it over the label as an eyebrow, which is the one position that
// reads before the method name rather than after it.
export type SurchargePosition =
  | 'inline'
  | 'trailing'
  | 'below'
  | 'above'
  | 'cornerStart'
  | 'cornerEnd';

// Where the rows drawn as the wallet's OWN button (Apple Pay / Google Pay with
// `native_element` applying in this render) are placed.
//
// Not within their section — they leave it. Every such row is lifted out of the
// catalogue into one express strip, and this says where on the payment surface
// that strip goes:
//
//   `top`         above everything, the custom fields included;
//   `belowFields` under the merchant's custom fields, still above the card form
//                 and every section;
//   `belowCard`   directly under the card form, above the remaining sections
//                 (with no card form in this render, it falls back to
//                 `belowFields`);
//   `bottom`      under the last section, the last thing offered.
//
// The rows keep their own relative order in the strip whichever end it sits at.
// Decided from the pane's setting for the render context, not from a later
// fallback to the tile, so a row never moves under the buyer.
export type NativeElementPosition = 'top' | 'belowFields' | 'belowCard' | 'bottom';

// How the figure is painted.
//
// `plain` is text in the configured colour. `badge` fills the shape with a tint
// of it. `outline` draws the border only — the middle weight, for a checkout
// whose tiles are already busy. `solid` fills with the colour itself and writes
// on it in whichever of black or white reads better, which is the loudest
// option and the one to reach for when the figure must not be missed.
// `underline` keeps the text weight of `plain` and adds a rule beneath it.
// `ribbon` runs the figure diagonally across a corner of the tile — the one
// treatment that reads before the label rather than beside it, for a merchant
// steering hard toward a cheaper provider.
export type SurchargeStyle = 'plain' | 'badge' | 'outline' | 'solid' | 'underline' | 'ribbon';

// The border drawn around the shapes `badge`, `outline`, `solid` and `ribbon`
// paint. `outline` draws nothing BUT its border, so `none` there would erase
// the style rather than restyle it and reads as `thin` instead.
export type SurchargeBorderWidth = 'none' | 'thin' | 'medium' | 'thick';

// How that border is drawn. `dashed` and `dotted` read as provisional, which
// suits a promotional discount; `solid` is the neutral default.
export type SurchargeBorderStyle = 'solid' | 'dashed' | 'dotted';

// The figure's type size, relative to the tile's own text.
export type SurchargeSize = 'sm' | 'md' | 'lg';

// The corner treatment of the shapes `badge`, `outline` and `solid` draw.
// Ignored by `plain` and `underline`, which draw no shape.
export type SurchargeShape = 'pill' | 'rounded' | 'square';

// How heavily the figure is set.
export type SurchargeWeight = 'regular' | 'medium' | 'bold';

// Whether the figure carries an explicit sign.
//
// `always` writes `+` on a surcharge and `−` on a discount (U+2212, not a
// hyphen, so it aligns with the digits). `minusOnly` writes the minus and
// leaves a surcharge unsigned, which reads as a price rather than as an
// adjustment. `none` writes neither and leans entirely on the two colours —
// legible only when a merchant configures one direction, and offered because a
// merchant who only ever discounts should not have to explain a plus sign.
export type SurchargeSign = 'always' | 'minusOnly' | 'none';

// Whether the figure names itself.
//
// `amount` is the figure alone. `labelled` prefixes the localized word for the
// direction ("Surcharge" / "Discount"), which costs horizontal room and buys a
// tile nobody has to interpret.
export type SurchargeLabelMode = 'amount' | 'labelled';

// WHICH figure the tile prints, and in which order when it prints both.
//
// A discount and a surcharge are the same mechanism pointed in opposite
// directions, but they are not the same message: a saving is something a
// merchant advertises and a percentage is the persuasive half of it ("20%
// off"), while a fee is something a merchant discloses and the money is the
// half the buyer actually cares about. So the two directions carry this
// setting separately — `discountFigure` and `surchargeFigure` — rather than
// sharing one.
//
// The two figures always describe the same money. The router derives the
// percentage from the charge rather than from the merchant's rule, so a fixed
// rule states its share of this cart and a taxed rate states the share the
// buyer actually pays. That is what makes showing either — or both — safe.
export type SurchargeFigureMode = 'amount' | 'percent' | 'amountThenPercent' | 'percentThenAmount';

/**
 * Whether the tile has room to print the wording alongside the figure.
 *
 * One pairing says no: the diagonal ribbon at either corner. That band is
 * clipped by the tile it crosses, and the readable length is set by how far the
 * corner is inset — roughly 75px on a standard payment tile, against roughly
 * 107px for a labelled figure at the default size. Printing the word there
 * clips the *figure*, and a half-shown price is worse than a plain one, so the
 * word is what gives way.
 *
 * Exported, and shared by the buyer-facing checkout and the dashboard's live
 * preview, because the two must reach the same answer. A preview that promises
 * a word the checkout will not print is the one failure a preview cannot have.
 */
export function surchargeShowsLabel(
  labelMode: SurchargeLabelMode,
  style: SurchargeStyle,
  position: SurchargePosition,
): boolean {
  if (labelMode !== 'labelled') return false;
  return !(style === 'ribbon' && (position === 'cornerStart' || position === 'cornerEnd'));
}

/** One of the two things a tile can print. */
export type SurchargeFigurePart = 'amount' | 'percent';

/** Exactly what one tile prints, in order. See {@link surchargeFigurePlan}. */
export interface SurchargeFigurePlan {
  /** The leading figure. Always present — a tile with nothing to say renders nothing at all. */
  first: SurchargeFigurePart;
  /** The trailing figure, parenthesised by the renderer, or `null` for one figure. */
  second: SurchargeFigurePart | null;
  /** Whether the localized direction word prefixes the whole thing. */
  label: boolean;
}

/**
 * What one tile prints: which figures, in which order, and whether the word
 * comes with them.
 *
 * One function rather than a rule per surface, because the buyer-facing tile
 * and the dashboard's live preview must reach the same answer. A preview that
 * shows a percentage the checkout will not print — or prints in the other
 * order — is the one failure a preview cannot have.
 *
 * Three things it decides, in order of how much they cost to get wrong:
 *
 *  * **No percentage available, no percentage printed.** `percentAvailable` is
 *    false when the router sent none, which happens on an order of zero. A
 *    merchant who asked for `percent` gets the amount instead — never a blank
 *    tile, and never `0%`. This is the fallback the whole design leans on: the
 *    amount is exact in every case, so it is what every degradation lands on.
 *  * **A corner ribbon prints one figure.** The band crossing a tile's corner
 *    is clipped to roughly 75px of readable length; two figures and a word do
 *    not fit, and clipping a *price* is worse than showing less of one. It
 *    keeps the merchant's leading figure — their own statement of which half
 *    matters — and drops the trailing one along with the word.
 *  * **Direction picks the setting.** `discountFigure` for a credit,
 *    `surchargeFigure` for a charge, because a saving and a fee are not the
 *    same message even though they are the same mechanism.
 */
export function surchargeFigurePlan(
  branding: Pick<
    CheckoutBranding,
    'surchargeFigure' | 'discountFigure' | 'surchargeLabel' | 'surchargeStyle' | 'surchargePosition'
  >,
  isDiscount: boolean,
  percentAvailable: boolean,
): SurchargeFigurePlan {
  const mode = isDiscount ? branding.discountFigure : branding.surchargeFigure;
  const label = surchargeShowsLabel(
    branding.surchargeLabel,
    branding.surchargeStyle,
    branding.surchargePosition,
  );

  // Money only, whatever was asked for: there is no second figure to state.
  if (!percentAvailable) return { first: 'amount', second: null, label };

  const ordered: Record<SurchargeFigureMode, [SurchargeFigurePart, SurchargeFigurePart | null]> = {
    amount: ['amount', null],
    percent: ['percent', null],
    amountThenPercent: ['amount', 'percent'],
    percentThenAmount: ['percent', 'amount'],
  };
  const [first, second] = ordered[mode];

  // The corner ribbon keeps the merchant's leading figure and nothing else.
  // `surchargeShowsLabel` has already dropped the word for the same reason, so
  // this is the same rule applied to the second figure.
  const cornerRibbon =
    branding.surchargeStyle === 'ribbon' &&
    (branding.surchargePosition === 'cornerStart' || branding.surchargePosition === 'cornerEnd');

  return { first, second: cornerRibbon ? null : second, label };
}

/**
 * Black or white, whichever reads better on `color` — for the `solid` style,
 * which writes on the configured colour itself.
 *
 * Picked by contrast rather than by a luminance threshold so the answer is the
 * one WCAG would give, and falls back to white on an unparseable colour, which
 * is what the rest of this module does with a colour it cannot read.
 */
export function readableInkOn(color: string): string {
  const onBlack = contrastRatio(color, '#000000');
  const onWhite = contrastRatio(color, '#ffffff');
  if (onBlack === null || onWhite === null) return '#ffffff';
  return onBlack >= onWhite ? '#000000' : '#ffffff';
}

/** Type size per {@link SurchargeSize}, relative to the tile's own text. */
const SURCHARGE_FONT_SIZE: Record<SurchargeSize, string> = {
  sm: '0.6875rem',
  md: '0.8125rem',
  lg: '0.9375rem',
};

/** Shape padding per {@link SurchargeSize}. Grows with the type. */
const SURCHARGE_PAD: Record<SurchargeSize, string> = {
  sm: '1px 6px',
  md: '2px 8px',
  lg: '3px 10px',
};

/** Corner radius per {@link SurchargeShape}. */
const SURCHARGE_RADIUS: Record<SurchargeShape, string> = {
  pill: '999px',
  rounded: '6px',
  square: '0px',
};

/** Border thickness per {@link SurchargeBorderWidth}. */
const SURCHARGE_BORDER_WIDTH: Record<SurchargeBorderWidth, string> = {
  none: '0px',
  thin: '1px',
  medium: '2px',
  thick: '3px',
};

/** Font weight per {@link SurchargeWeight}. */
const SURCHARGE_FONT_WEIGHT: Record<SurchargeWeight, string> = {
  regular: '400',
  medium: '600',
  bold: '700',
};

/**
 * The values each disclosure axis accepts, in the order a picker should offer
 * them: quietest first, so a merchant scrolling down is turning the figure up.
 *
 * Exported because both the dashboard's picker and this module's decoder need
 * the same list, and a picker offering a value the decoder rejects is a control
 * that silently does nothing.
 */
export const SURCHARGE_POSITIONS: readonly SurchargePosition[] = [
  'trailing',
  'inline',
  'below',
  'above',
  'cornerStart',
  'cornerEnd',
];
// Ordered as they read down the page, which is the order the dashboard's select
// offers them in.
export const NATIVE_ELEMENT_POSITIONS: readonly NativeElementPosition[] = [
  'top',
  'belowFields',
  'belowCard',
  'bottom',
];
export const SURCHARGE_STYLES: readonly SurchargeStyle[] = [
  'plain',
  'underline',
  'outline',
  'badge',
  'solid',
  'ribbon',
];
export const SURCHARGE_BORDER_WIDTHS: readonly SurchargeBorderWidth[] = [
  'none',
  'thin',
  'medium',
  'thick',
];
export const SURCHARGE_BORDER_STYLES: readonly SurchargeBorderStyle[] = [
  'solid',
  'dashed',
  'dotted',
];
export const SURCHARGE_SIZES: readonly SurchargeSize[] = ['sm', 'md', 'lg'];

/**
 * Whether a style draws a shape behind the figure, and therefore whether
 * {@link SurchargeShape} means anything for it.
 *
 * `plain` and `underline` paint text. A dashboard that leaves the corner picker
 * live under them offers a control that changes nothing on screen, which is how
 * a merchant decides the whole panel is broken; one that hides it loses their
 * choice when they switch back. Disabled is the answer, and this is the
 * predicate it needs.
 */
export function surchargeStyleDrawsAShape(style: SurchargeStyle): boolean {
  return style === 'badge' || style === 'outline' || style === 'solid';
}
export const SURCHARGE_SHAPES: readonly SurchargeShape[] = ['pill', 'rounded', 'square'];
export const SURCHARGE_WEIGHTS: readonly SurchargeWeight[] = ['regular', 'medium', 'bold'];
export const SURCHARGE_SIGNS: readonly SurchargeSign[] = ['always', 'minusOnly', 'none'];
export const SURCHARGE_LABEL_MODES: readonly SurchargeLabelMode[] = ['amount', 'labelled'];
export const SURCHARGE_FIGURE_MODES: readonly SurchargeFigureMode[] = [
  'amount',
  'percent',
  'amountThenPercent',
  'percentThenAmount',
];

/**
 * The buyer locales the checkout ships copy for, and therefore the ones a
 * merchant can override.
 *
 * Driven from here rather than restated in the dashboard, so a locale added to
 * the checkout cannot leave the dashboard offering one fewer box than the
 * buyer-facing render reads.
 */
export const CHECKOUT_LOCALES = [
  'en',
  'de',
  'fr',
  'es',
  'it',
  'nl',
  // Simplified and traditional are separate dictionaries in the checkout,
  // not region variants: they use different characters, so a merchant who
  // writes one has not written the other.
  'zh',
  'zh-Hant',
  'hi',
  'ar',
] as const;
export type CheckoutLocale = (typeof CHECKOUT_LOCALES)[number];

/**
 * Locales written right-to-left, for a UI that renders a merchant's override
 * next to the others.
 *
 * A dashboard field holding Arabic needs `dir="rtl"` on the input itself:
 * without it the caret jumps, punctuation lands on the wrong end, and what the
 * merchant types is not what the buyer will see. Exported rather than kept in
 * the dashboard so the buyer-facing checkout and the editor agree on which
 * locales that is.
 */
export const RTL_CHECKOUT_LOCALES: readonly CheckoutLocale[] = ['ar'];

/**
 * `dir` for a checkout locale — `'rtl'` for Arabic, `'ltr'` for the rest.
 *
 * Resolved on the **base language**, the same way `checkoutCopy` resolves an
 * override. Comparing the full string looked equivalent and was not: a buyer
 * sending `ar-EG` gets the `ar` copy from `checkoutCopy` and would have got
 * `ltr` from here — Arabic text in a left-to-right layout, which is the exact
 * failure this function exists to prevent. Case is folded for the same reason;
 * `Accept-Language` may arrive as `AR`.
 */
export function checkoutLocaleDir(locale: string): 'ltr' | 'rtl' {
  const base = String(locale ?? '')
    .toLowerCase()
    .split('-')[0];
  return (RTL_CHECKOUT_LOCALES as readonly string[]).includes(base) ? 'rtl' : 'ltr';
}

/**
 * The copy fields a merchant may write once per locale.
 *
 * Each has a flat `<field>` and a `<field>Translations` map beside it. The flat
 * value is what every existing merchant already has and stays authoritative
 * wherever no override is set — which is what makes this additive: a profile
 * saved before translations existed renders exactly as it did, in every locale.
 */
export const LOCALIZABLE_COPY_FIELDS = [
  'headerText',
  'payButtonLabel',
  'cardTermsMessage',
  'footerText',
] as const;
export type LocalizableCopyField = (typeof LOCALIZABLE_COPY_FIELDS)[number];

/** The translations key that belongs to a copy field. */
export function copyTranslationsKey(
  field: LocalizableCopyField,
): `${LocalizableCopyField}Translations` {
  return `${field}Translations`;
}

/**
 * One piece of merchant copy, in the buyer's locale.
 *
 * Exact locale wins (`de-AT`), then its base language (`de`), then the flat
 * field — the same order `customFieldText` resolves in, because a merchant who
 * has learnt one of these should not have to learn the other.
 *
 * Returning the flat value rather than `''` is the backward-compatibility
 * guarantee: an override is an addition to what a merchant already wrote, never
 * a replacement for it, so an empty or missing map changes nothing.
 */
export function checkoutCopy(
  branding: CheckoutBranding,
  field: LocalizableCopyField,
  locale?: string,
): string {
  const translated = translationFor(branding[copyTranslationsKey(field)], locale);
  return translated ?? branding[field];
}

/**
 * The merchant's own word for one direction, or `null` to use the checkout's
 * built-in one.
 *
 * `null` rather than a default string because the fallback is *localized* and
 * lives in the buyer-facing bundle — this module has no i18n of its own, and
 * inventing an English default here would print "Discount" to a German buyer.
 *
 * A merchant who sets nothing therefore keeps the shipped wording in both
 * languages, which is the intended default and already reads well.
 */
export function surchargeWordFor(
  branding: Pick<CheckoutBranding, 'surchargeWordTranslations' | 'discountWordTranslations'>,
  isDiscount: boolean,
  locale?: string,
): string | null {
  return translationFor(
    isDiscount ? branding.discountWordTranslations : branding.surchargeWordTranslations,
    locale,
  );
}

export interface TrustBadge {
  id: string;
  label: string;
  textColor: string;
  backgroundColor: string;
  borderColor: string | null;
}

// --- Notices -------------------------------------------------
//
// A notice is one short reassurance line on the checkout — a line icon and a
// text — in one of two places. It replaces the trust badges as the default:
// a badge carries its own three colours, so every theme had to recolour the
// stock pair (F-BRAND-24) and a dark theme still wore light chips; a notice
// carries none and is drawn in the checkout's own muted text and accent
// colour, so it follows whatever palette the merchant picks. Its text is
// written per locale, which a badge's never was.
//
// Persisted JSON-stringified under `sdk_ui_rules.branding.notices`, the same
// transport as `trustBadges` and `customFields`.

/**
 * Where a notice is drawn.
 *
 * - `summary`: under the order summary in the split layout; under the compact
 *   header when there is no summary pane.
 * - `payment`: at the bottom of the payment element, under the pay button.
 */
export type NoticeSection = 'summary' | 'payment';
export const NOTICE_SECTIONS: readonly NoticeSection[] = ['summary', 'payment'];

/**
 * How many notices one section draws. More than three stops reading as
 * reassurance and starts reading as small print, so the decoder drops the
 * overflow and the editor refuses a fourth.
 */
export const NOTICES_PER_SECTION = 3;

/** Longest notice text, per locale. A notice is one line, not a paragraph. */
export const NOTICE_LABEL_MAX_LENGTH = 80;

/** The marks a notice can carry, in the order the editor's grid offers them. */
export const NOTICE_ICONS = [
  'lock',
  'shield',
  'check',
  'truck',
  'package',
  'refresh',
  'star',
  'support',
  'globe',
  'tag',
  'heart',
  'leaf',
  'clock',
  'bank',
] as const;
export type NoticeIcon = (typeof NOTICE_ICONS)[number];

/** What an unknown or missing icon decodes to. */
export const NOTICE_ICON_FALLBACK: NoticeIcon = 'check';

/**
 * Each mark as SVG path data on a 24×24 grid, stroked (not filled) with round
 * caps and joins.
 *
 * Kept here rather than drawn twice so the checkout and the customizer's icon
 * grid cannot disagree about what "shield" looks like: both render these
 * strings, and a mark added to `NOTICE_ICONS` without paths is a type error.
 * Every shape is a `<path>` — circles, rectangles and polylines included —
 * so a renderer needs one element type and no per-icon template.
 */
export const NOTICE_ICON_PATHS: Readonly<Record<NoticeIcon, readonly string[]>> = {
  lock: [
    'M6 10h12a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2z',
    'M8 10V7a4 4 0 0 1 8 0v3',
  ],
  shield: ['M12 3l7 3v6c0 4.2-2.9 7.6-7 9-4.1-1.4-7-4.8-7-9V6z', 'M9 12l2.5 2.5 4-4.5'],
  check: ['M21 12a9 9 0 1 1-18 0a9 9 0 1 1 18 0z', 'M8 12.5l3 3 5-6'],
  truck: [
    'M3 7.5h10v9H3z',
    'M13 11h4l3 3v2.5h-7z',
    'M8.8 18a1.8 1.8 0 1 1-3.6 0a1.8 1.8 0 1 1 3.6 0z',
    'M18.8 18a1.8 1.8 0 1 1-3.6 0a1.8 1.8 0 1 1 3.6 0z',
  ],
  package: [
    'M11 21.73a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73z',
    'M12 22V12',
    'M3.29 7L12 12l8.71-5',
  ],
  refresh: ['M20 12a8 8 0 1 1-2.5-5.8', 'M20 4v5h-5'],
  star: ['m12 3.6 2.6 5.3 5.9.9-4.3 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8-4.3-4.1 5.9-.9z'],
  support: [
    'M5 13v-1a7 7 0 0 1 14 0v1',
    'M3.5 14.5a1.8 1.8 0 0 1 1.8-1.8H7v5H5.3a1.8 1.8 0 0 1-1.8-1.8z',
    'M20.5 14.5a1.8 1.8 0 0 0-1.8-1.8H17v5h1.7a1.8 1.8 0 0 0 1.8-1.8z',
    'M19 17.7v.8a2.5 2.5 0 0 1-2.5 2.5H13',
  ],
  globe: [
    'M21 12a9 9 0 1 1-18 0a9 9 0 1 1 18 0z',
    'M3 12h18',
    'M12 3a14 14 0 0 1 0 18a14 14 0 0 1 0-18z',
  ],
  tag: ['M11 3H4v7l10 10 7-7z', 'M9.1 7a1.3 1.3 0 1 1-2.6 0a1.3 1.3 0 1 1 2.6 0z'],
  heart: ['M12 20s-7-4.4-7-9.3A3.9 3.9 0 0 1 12 8a3.9 3.9 0 0 1 7 2.7c0 4.9-7 9.3-7 9.3z'],
  leaf: ['M20 4c0 9-5.4 13-9.5 13A4.5 4.5 0 0 1 6 12.5C6 7 12 4 20 4z', 'M4 20c2-4.5 5-7.5 9-9.5'],
  clock: ['M21 12a9 9 0 1 1-18 0a9 9 0 1 1 18 0z', 'M12 7v5l3 2'],
  bank: ['M3 9.5 12 4l9 5.5', 'M5 10v8M9.5 10v8M14.5 10v8M19 10v8', 'M3 20h18'],
};

export function isNoticeIcon(value: unknown): value is NoticeIcon {
  return typeof value === 'string' && (NOTICE_ICONS as readonly string[]).includes(value);
}

export interface CheckoutNotice {
  /** Stable across edits and drags; `[A-Za-z0-9_-]`, at most 40 characters. */
  id: string;
  section: NoticeSection;
  icon: NoticeIcon;
  /** Default-language text, and what every locale without an override reads. */
  label: string;
  /** Per-locale overrides of `label`; a blank entry means "use `label`". */
  labelTranslations: CustomFieldTranslations;
}

// Merchant-defined checkout inputs. Rendered by the buyer-facing checkout
// above the payment surface; submitted values land in the payment's
// `metadata` under each field's `key`. Persisted JSON-stringified under
// `sdk_ui_rules.branding.customFields` (same transport as trustBadges).
// `checkbox` is a single opt-in box (terms acceptance, marketing consent),
// not a multi-select. Its submitted value is always the string `'true'` or
// `'false'` — never absent — so a merchant can tell "declined" from "never
// asked" in the payment's metadata, which is what makes it usable as a
// consent record. See `CHECKBOX_CHECKED` / `isCheckboxChecked`.
export type CustomFieldType = 'text' | 'textarea' | 'password' | 'email' | 'select' | 'checkbox';

// Per-locale overrides ('de', 'en', …). Missing locale falls back to the
// default-language string on the field itself — see `customFieldText`.
export type CustomFieldTranslations = Record<string, string>;

export interface CustomFieldOption {
  value: string;
  label: string;
  labelTranslations: CustomFieldTranslations;
}

// --- Conditional visibility ---------------------------------------------
//
// A field can be gated on facts the payment already carries when the
// checkout renders: the intent's `metadata` (which a shop integration fills
// per product/category — see the WordPress plugin's product metadata), its
// `currency`, or its `amount`. This is what lets one profile serve a
// "Windows key" order (no fields) and a "Spotify account" order (login
// fields) without two shops.
//
// Evaluation is authoritative on the **backend**: `form_payment_link_data`
// drops non-matching fields from the payload the buyer's browser receives,
// so merchant metadata never leaves the server and the rules can't be
// tampered with client-side. The implementation here is the shared
// specification (and drives the control-center's builder); the Rust mirror
// in `crates/router/src/core/payment_link/custom_fields.rs` must stay
// behaviorally identical.
export type CustomFieldConditionSource = 'metadata' | 'currency' | 'amount';

// String operators apply to metadata + currency; numeric ones (`gt`…`lte`)
// apply to amount and to metadata values that parse as numbers.
export type CustomFieldOperator =
  | 'equals'
  | 'not_equals'
  | 'contains'
  | 'not_contains'
  | 'starts_with'
  | 'ends_with'
  | 'in'
  | 'not_in'
  | 'exists'
  | 'not_exists'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte';

export interface CustomFieldCondition {
  // Stable identity for editor list operations (reorder/remove). Not part
  // of the semantics.
  id: string;
  source: CustomFieldConditionSource;
  // Metadata key to read. Ignored (and persisted empty) for currency/amount.
  key: string;
  operator: CustomFieldOperator;
  // Right-hand operand. `in`/`not_in` read it as a comma-separated list;
  // `exists`/`not_exists` ignore it; amount comparisons parse it as an
  // integer in the currency's minor unit (1000 = 10.00).
  value: string;
}

export interface CustomFieldVisibility {
  // `always` — unconditional (the default, and what every pre-feature field
  // decodes to). `match` — evaluate `conditions`.
  mode: 'always' | 'match';
  // How to combine multiple conditions.
  match: 'all' | 'any';
  conditions: CustomFieldCondition[];
}

export interface CheckoutCustomField {
  // Stable identity for editor list operations (reorder/remove).
  id: string;
  // Metadata key the submitted value is stored under. Must be unique per
  // profile; the editor enforces `CUSTOM_FIELD_KEY_PATTERN`.
  key: string;
  type: CustomFieldType;
  // Default-language copy; `*Translations` maps override per locale.
  label: string;
  labelTranslations: CustomFieldTranslations;
  placeholder: string;
  placeholderTranslations: CustomFieldTranslations;
  helpText: string;
  helpTextTranslations: CustomFieldTranslations;
  required: boolean;
  // Disabled fields stay configured but are neither rendered nor submitted.
  enabled: boolean;
  // Length bounds apply to text/textarea/password/email; null = unbounded.
  minLength: number | null;
  maxLength: number | null;
  // Prefill for text-like fields; for selects, the option `value` selected
  // initially (empty = placeholder "choose" state).
  defaultValue: string;
  // Select choices; ignored for other types.
  options: CustomFieldOption[];
  // Conditional display. Always populated by the decoder — fields without a
  // persisted rule decode to `{ mode: 'always', … }`.
  visibility: CustomFieldVisibility;
}

export interface CheckoutBranding {
  // Brand identity
  displayName: string;
  logoUrl: string;
  tagline: string;
  showLogo: boolean;
  logoShape: LogoShape;
  logoSize: LogoSize;

  // Color tokens
  primary: string;
  background: string;
  surface: string;
  text: string;
  heading: string;
  muted: string;
  border: string;
  accentText: string;
  buttonBackground: string;
  buttonText: string;

  // Typography
  fontFamily: FontFamily;
  headingWeight: FontWeight;

  // Shape — granular per-element. Surfaces & inputs use a narrower union
  // (no pill) because pill cards/inputs are always wrong; button and badge
  // keep the full `CornerRadius` since pill is a legitimate look there.
  radiusSurface: NonPillRadius;
  radiusInput: NonPillRadius;
  radiusButton: CornerRadius;
  radiusBadge: CornerRadius;
  surfaceStyle: SurfaceStyle;

  // Spacing — four independent dimensions.
  surfacePadding: SpacingScale;
  verticalGap: SpacingScale;
  inputSize: SizeScale;
  buttonSize: SizeScale;

  // Layout
  layout: LayoutStyle;
  summaryPosition: SummaryPosition;
  showOrderSummary: boolean;
  summaryGradient: boolean;
  showTotal: boolean;
  totalLabel: string;
  showCurrencyCode: boolean;
  showOrderItems: boolean;

  // Hosted-checkout exit. It is only drawn when the payment resolves a
  // cancel URL; these fields decide whether and how that destination is shown.
  cancelLinkEnabled: boolean;
  cancelLinkPosition: 'top' | 'bottom';
  cancelLinkStyle: 'link' | 'button';

  // Provider surcharge / discount disclosure. `showSurcharge` false hides the
  // per-tile figure entirely; the in-pane breakdown is unaffected, since that
  // one is the merchant's existing legal disclosure rather than a steering
  // signal. The two colours are separate on purpose: a surcharge and a discount
  // are opposite news for the buyer and reading them in one colour is exactly
  // the mistake a minus sign is easy to miss in.
  showSurcharge: boolean;
  surchargePosition: SurchargePosition;
  // Where the express strip of wallet buttons goes on the payment surface.
  // Only rows that actually draw a button are lifted into it; a surface with
  // none has no strip.
  nativeElementPosition: NativeElementPosition;
  // One button per line in that strip, instead of side by side.
  //
  // Off by default, because two wallets on one line is what the strip is for:
  // Apple Pay and Google Pay are one choice made twice, and a buyer is offered
  // at most one of them on any given browser anyway — the second track is
  // usually the card form arriving sooner rather than a second button. On for
  // a merchant who wants each button full width: a wide brand mark, a checkout
  // narrow enough that two tracks crowd, or simply their own taste.
  //
  // The strip already stacks on its own when the surface is too narrow to seat
  // two; this makes it stack at every width.
  nativeElementStacked: boolean;
  surchargeStyle: SurchargeStyle;
  surchargeSize: SurchargeSize;
  surchargeShape: SurchargeShape;
  surchargeBorderWidth: SurchargeBorderWidth;
  surchargeBorderStyle: SurchargeBorderStyle;
  surchargeWeight: SurchargeWeight;
  surchargeSign: SurchargeSign;
  surchargeUppercase: boolean;
  surchargeLabel: SurchargeLabelMode;
  surchargeFigure: SurchargeFigureMode;
  discountFigure: SurchargeFigureMode;
  surchargeWordTranslations: CustomFieldTranslations;
  discountWordTranslations: CustomFieldTranslations;
  surchargeColor: string;
  discountColor: string;

  // Trust badges (fully customizable; empty = hide row). No longer a default:
  // new shops start with none and get `notices` instead; a shop that customised
  // its badges keeps them. See `resolveNoticesAndBadges`.
  trustBadges: TrustBadge[];

  // Reassurance lines — icon + per-locale text — in the summary and at the
  // bottom of the payment element (empty = none).
  notices: CheckoutNotice[];

  // Merchant-defined checkout inputs (empty = no custom fields section).
  customFields: CheckoutCustomField[];

  // Where each payment-method section renders, 1-100, lower first.
  methodSectionOrder: MethodSectionOrder;

  // The merchant's name for a section, per locale. Empty = use the built-in
  // name, or the id itself for a section with none.
  methodSectionLabels: MethodSectionLabels;

  // Copy. All of these are optional in the persisted form: empty string
  // means "unset" and consumers should fall back (e.g. payButtonLabel falls
  // back to a localized "Pay $X" string).
  headerText: string;
  headerTextTranslations: CustomFieldTranslations;
  payButtonLabel: string;
  payButtonLabelTranslations: CustomFieldTranslations;
  cardTermsMessage: string;
  cardTermsMessageTranslations: CustomFieldTranslations;
  footerText: string;
  footerTextTranslations: CustomFieldTranslations;
  supportEmail: string;

  // Stripe Elements behavior. `paymentLayout` only affects what's painted
  // inside Stripe's iframe; non-Stripe connector panes stack vertically
  // regardless. `labelStyle` is also Stripe-iframe scope.
  paymentLayout: PaymentLayout;
  labelStyle: LabelStyle;

  // The Airwallex card surface. Scoped to that pane by name because that is
  // the only rail these reach: Stripe's element is themed through its own
  // Appearance API and the vault's fields are our DOM, so a field weight
  // written here would quietly do nothing on either. See `airwallexCard.ts`
  // for the geometry these sit on top of.
  //
  // `airwallexCardPreset` is the only one that can be EMPTY, and that is the
  // hinge of the whole migration: empty means "the merchant has not chosen
  // here", which lets the connector account's own `integration_style` keep
  // deciding for every shop configured before this section existed. A default
  // of `split_fields_labelled` on this field would override all of them
  // silently. `resolveAirwallexCardPreset` is the resolution.
  airwallexCardPreset: AirwallexCardPreset | '';
  // Removes the card form from the shop's checkout entirely. Money-affecting
  // and therefore off by default: with this on, no buyer can pay by card.
  airwallexHideCard: boolean;
  // Drops the "Card" heading over the Airwallex form. Presentation only, and
  // scoped to this rail because it is this rail's form the merchant is looking
  // at when they turn it off: a shop whose form is the first thing on the
  // checkout has no use for a word naming what the fields obviously are.
  //
  // The section is what loses its NAME, not its disclosure — a surcharge or
  // discount chip on the card rail still renders on that line, because what it
  // says is the money the buyer is about to be charged.
  airwallexHideCardTitle: boolean;
  airwallexFieldChrome: AirwallexFieldChrome;
  // Empty means "whatever the preset says" — see `resolveAirwallexLabelStyle`.
  airwallexLabelStyle: AirwallexLabelStyle | '';
  airwallexFieldFontSize: AirwallexFieldFontSize;
  airwallexFieldWeight: AirwallexFieldWeight;
  airwallexFocusRing: AirwallexFocusRing;
  airwallexAutofill: AirwallexAutofillTreatment;
  airwallexValidTint: boolean;
  // Decorative marks, and nothing more — they do not narrow what Airwallex
  // accepts. See `AIRWALLEX_NETWORK_MARKS`.
  airwallexNetworkMarks: boolean;

  // The Stripe Payment Element's own look (`cardFormStyles.ts`). Applied over
  // the colour, font and radius tokens above, inside Stripe's iframe.
  stripeTheme: StripeTheme;
  stripeInputs: StripeInputStyle;
  stripeLabels: StripeLabelPlacement;
  stripeSpacing: StripeSpacing;
  stripeFieldFontSize: CardFieldFontSize;
  stripeFieldWeight: CardFieldWeight;
  stripeFocusRing: CardFocusRing;
  stripeDisableAnimations: boolean;

  // PayPal Card Fields. The box, captions and rows are the checkout's DOM;
  // the type size and weight reach the text inside PayPal's iframes.
  paypalCardLayout: PaypalCardLayout;
  paypalLabelStyle: PaypalLabelStyle;
  paypalFieldChrome: CardFieldChrome;
  paypalFieldFontSize: CardFieldFontSize;
  paypalFieldWeight: CardFieldWeight;
  paypalFocusRing: CardFocusRing;
  paypalValidTint: boolean;

  // NomuPay's COPYandPAY widget: `wpwlOptions` for the form, the checkout's
  // CSS on its `.wpwl-*` boxes, and the Apple Pay and Google Pay buttons the
  // same widget draws. `auto` follows the checkout's theme, as before.
  nomupayFormStyle: NomupayFormStyle;
  nomupayShowLabels: boolean;
  nomupayShowPlaceholders: boolean;
  nomupayShowCvvHint: boolean;
  nomupayFieldChrome: CardFieldChrome;
  nomupayFieldFontSize: CardFieldFontSize;
  nomupayFocusRing: CardFocusRing;
  nomupayApplePayButtonStyle: ApplePayButtonStyle;
  nomupayApplePayButtonType: ApplePayButtonType;
  nomupayGooglePayButtonColor: GooglePayButtonColor;
  nomupayGooglePayButtonType: GooglePayButtonType;

  // Footer
  showPoweredBy: boolean;

  // Advanced — raw CSS appended after brand-token CSS variables are
  // applied, so its declarations win cascade order. Sanitized at render
  // time (see `sanitizeCustomCss`); persisted as-typed under
  // `sdk_ui_rules.branding.customCss`.
  customCss: string;
}

// --- Token maps ---------------------------------------------------------

// Self-hostable font stacks. The buyer-facing checkout bundles these via
// @fontsource* packages; the variable-font names ('Inter Variable', …) are
// listed first so the smaller variable file is preferred when bundled.
const FONT_STACKS: Record<FontFamily, string> = {
  inter: "'Inter Variable', 'Inter', system-ui, -apple-system, sans-serif",
  system:
    "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif",
  serif: "Georgia, 'Times New Roman', serif",
  mono: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  roboto: "'Roboto', system-ui, sans-serif",
  poppins: "'Poppins', system-ui, sans-serif",
  manrope: "'Manrope Variable', 'Manrope', system-ui, sans-serif",
  'dm-sans': "'DM Sans Variable', 'DM Sans', system-ui, sans-serif",
  'space-grotesk': "'Space Grotesk Variable', 'Space Grotesk', system-ui, sans-serif",
  'plex-sans': "'IBM Plex Sans', system-ui, sans-serif",
  'work-sans': "'Work Sans Variable', 'Work Sans', system-ui, sans-serif",
  'open-sans': "'Open Sans Variable', 'Open Sans', system-ui, sans-serif",
  lora: "'Lora Variable', 'Lora', Georgia, serif",
  playfair: "'Playfair Display Variable', 'Playfair Display', Georgia, serif",
  'plex-mono': "'IBM Plex Mono', ui-monospace, monospace",
  'jetbrains-mono': "'JetBrains Mono Variable', 'JetBrains Mono', ui-monospace, monospace",
};

const RADIUS_PX: Record<CornerRadius, string> = {
  square: '0px',
  small: '6px',
  medium: '12px',
  large: '20px',
  pill: '999px',
};

const FONT_WEIGHT_NUMERIC: Record<FontWeight, string> = {
  regular: '400',
  medium: '500',
  semibold: '600',
  bold: '700',
};

const SURFACE_PAD: Record<SpacingScale, string> = {
  compact: '1rem',
  comfortable: '1.5rem',
  spacious: '2rem',
};

const VERTICAL_GAP: Record<SpacingScale, string> = {
  compact: '0.75rem',
  comfortable: '1rem',
  spacious: '1.5rem',
};

const INPUT_PAD: Record<SizeScale, string> = {
  sm: '0.5rem 0.75rem',
  md: '0.625rem 0.75rem',
  lg: '0.875rem 0.875rem',
};

const BUTTON_PAD: Record<SizeScale, string> = {
  sm: '0.625rem 1rem',
  md: '0.875rem 1.25rem',
  lg: '1.125rem 1.5rem',
};

export function fontStack(family: FontFamily): string {
  return FONT_STACKS[family] ?? FONT_STACKS.inter;
}

export function radiusValue(radius: CornerRadius): string {
  return RADIUS_PX[radius] ?? RADIUS_PX.medium;
}

export function fontWeightValue(weight: FontWeight): string {
  return FONT_WEIGHT_NUMERIC[weight] ?? '600';
}

export function surfacePadValue(scale: SpacingScale): string {
  return SURFACE_PAD[scale] ?? SURFACE_PAD.comfortable;
}

export function verticalGapValue(scale: SpacingScale): string {
  return VERTICAL_GAP[scale] ?? VERTICAL_GAP.comfortable;
}

export function inputPadValue(size: SizeScale): string {
  return INPUT_PAD[size] ?? INPUT_PAD.md;
}

export function buttonPadValue(size: SizeScale): string {
  return BUTTON_PAD[size] ?? BUTTON_PAD.md;
}

export function logoDimensions(size: LogoSize): { px: number; radius: number } {
  switch (size) {
    case 'sm':
      return { px: 36, radius: 8 };
    case 'lg':
      return { px: 64, radius: 16 };
    case 'md':
    default:
      return { px: 48, radius: 12 };
  }
}

const HEX_RE = /^#[0-9a-fA-F]{3}([0-9a-fA-F]{3})?$/;

export function isHexColor(value: string): boolean {
  return HEX_RE.test(value.trim());
}

/**
 * A CSS `<hex-color>`: whole token, leading `#` REQUIRED.
 *
 * ⚠️ The `#` is not pedantry here. `applyBrandingVariables` writes branding
 * values into custom properties unchanged (`el.style.setProperty(k, v)`), and
 * a custom property accepts any token stream — it is only interpreted when
 * substituted into a typed property, where `000` is not a colour and the
 * declaration is dropped. So a contrast API that accepted `'000'` would hand
 * back `21` for a pair that renders as nothing at all: approving a palette
 * that never reaches the screen is worse than refusing to judge it.
 */
const CSS_HEX_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

/**
 * The same shape with the `#` optional, for {@link isDarkSurface} ONLY.
 *
 * That function has stripped an optional hash since it was written, and
 * `'5a5a5a'` is a real colour whose answer is correct — tightening it onto the
 * CSS form would answer correct input wrongly. New API does not inherit the
 * tolerance; the existing caller keeps it.
 */
const LENIENT_HEX_RE = /^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

/** `[r, g, b]` in 0–255, or `null` when the token is not a colour. */
function parseHexChannels(value: string, pattern: RegExp): [number, number, number] | null {
  const match = pattern.exec(value.trim());
  if (!match) return null;
  const digits = match[1] as string;
  const full =
    digits.length === 3
      ? digits
          .split('')
          .map((c) => c + c)
          .join('')
      : digits;
  return [
    parseInt(full.slice(0, 2), 16),
    parseInt(full.slice(2, 4), 16),
    parseInt(full.slice(4, 6), 16),
  ];
}

/** WCAG 2.1 relative luminance (1.4.3), with the sRGB linearisation. */
function relativeLuminance([r, g, b]: [number, number, number]): number {
  const [lr, lg, lb] = [r, g, b].map((channel) => {
    const c = channel / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  return 0.2126 * lr + 0.7152 * lg + 0.0722 * lb;
}

/** Minimum contrast for body text — WCAG 2.1 success criterion 1.4.3. */
export const WCAG_AA_TEXT = 4.5;

/**
 * Minimum contrast for non-text UI — WCAG 2.1 success criterion 1.4.11.
 * Focus rings, borders, icons carrying meaning.
 */
export const WCAG_AA_UI = 3;

/**
 * WCAG 2.1 contrast ratio between two colours, 1–21. Order does not matter.
 *
 * Compare against {@link WCAG_AA_TEXT} or {@link WCAG_AA_UI}:
 *
 * ```ts
 * const ratio = contrastRatio(branding.surface, branding.text);
 * if (ratio === null || ratio < WCAG_AA_TEXT) useDefaultPalette();
 * ```
 *
 * ⚠️ Returns `null` — never a number — when either colour cannot be parsed.
 * That is the load-bearing part of this signature. Every colour bug this API
 * exists to prevent came from a helper answering with a plausible value for
 * something it could not read, and a `0` or a `21` here would be silently
 * believed by exactly the branch that is deciding whether text is legible.
 *
 * Accepts `#rgb` and `#rrggbb` — a CSS `<hex-color>`. ⚠️ A hash-less token is
 * `null`, not a ratio: `applyBrandingVariables` writes branding values into
 * custom properties verbatim, so `'000'` reaches CSS as something that is not a
 * colour. Returning `21` for it would approve a palette that never renders.
 * ({@link isDarkSurface} keeps its historical hash-less tolerance; this is new
 * API and does not inherit it.)
 */
export function contrastRatio(a: string, b: string): number | null {
  const first = parseHexChannels(a, CSS_HEX_RE);
  const second = parseHexChannels(b, CSS_HEX_RE);
  if (!first || !second) return null;
  const [x, y] = [relativeLuminance(first), relativeLuminance(second)];
  const lighter = Math.max(x, y);
  const darker = Math.min(x, y);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Crude perceived-luminance check — picks the right Stripe Elements preset
 * (`'night'` vs `'stripe'`) when the merchant has a dark surface.
 *
 * ⚠️ **This is not a readability test. Use {@link contrastRatio} for that.**
 * It is a YIQ luma split at 128, and on real brand colours its verdict and
 * actual legibility are not merely imprecise relative to each other — they can
 * be anti-correlated:
 *
 * | colour | `isDarkSurface` | vs white | |
 * | --- | --- | --- | --- |
 * | `#ff00ff` | `true` | 3.14:1 | fails AA text |
 * | `#00ff00` | `false` | 1.37:1 | fails AA text |
 * | `#0000ff` | `true` | 8.59:1 | passes |
 *
 * A caller picking text colour from this gets the wrong answer on both failing
 * cases. It is kept because choosing a Stripe preset is a legitimate and
 * different question.
 *
 * Inputs that are not a 3-/6-digit hex fall through to "light", the safer
 * assumption for the default palette.
 */
export function isDarkSurface(color: string): boolean {
  // ⚠️ Was a length check plus `parseInt` plus `Number.isNaN`, which let six
  // values the package's own validator rejects through as "dark" — `parseInt`
  // accepts a valid prefix, so `parseInt('0g', 16)` is `0`, not `NaN`. The
  // comment above already promised these fall through to "light"; now they do.
  const channels = parseHexChannels(color, LENIENT_HEX_RE);
  if (!channels) return false;
  const [r, g, b] = channels;
  const luma = (r * 299 + g * 587 + b * 114) / 1000;
  return luma < 128;
}

// --- Defaults -----------------------------------------------------------

// Light-mode DeloPay-branded baseline. Hex values come straight from
// `design-guidelines/colors.md`:
//   - #1E4FEB is `--color-dp-blue`, the wordmark/icon brand blue
//   - #0A1130 is `--color-dp-blue-dark`, the wordmark navy
//   - the rest are slate-50 / slate-200 / slate-500 / slate-800 from the
//     design system's neutral ramp.
// Picked for: high contrast, neutral surroundings, brand-blue accents,
// confident navy CTA. Reads "fintech" without feeling cold.
const LIGHT_PALETTE = {
  primary: '#1E4FEB',
  background: '#f8fafc',
  surface: '#ffffff',
  text: '#1e293b',
  heading: '#0A1130',
  muted: '#64748b',
  border: '#e2e8f0',
  accentText: '#1E4FEB',
  buttonBackground: '#0A1130',
  buttonText: '#ffffff',
} as const;

export const DEFAULT_BADGES: TrustBadge[] = [
  {
    id: 'secure',
    label: 'Secure',
    textColor: '#047857',
    backgroundColor: '#ecfdf5',
    borderColor: '#a7f3d0',
  },
  {
    id: 'ssl',
    label: '256-bit SSL',
    textColor: '#1E4FEB',
    backgroundColor: '#eff6ff',
    borderColor: '#bfdbfe',
  },
];

// Dark-mode equivalents of DEFAULT_BADGES. Dark trust chips need solid hex
// (the form's color picker only accepts hex), so we pick the design system's
// success-900 / info-900 backdrops paired with their *-400 foreground tokens.
export const DEFAULT_BADGES_DARK: TrustBadge[] = [
  {
    id: 'secure',
    label: 'Secure',
    textColor: '#34d399',
    backgroundColor: '#064e3b',
    borderColor: '#065f46',
  },
  {
    id: 'ssl',
    label: '256-bit SSL',
    textColor: '#60a5fa',
    backgroundColor: '#172554',
    borderColor: '#1e40af',
  },
];

/**
 * The notices every shop starts with.
 *
 * The same two claims the stock badges made — the payment is secure, the
 * connection is TLS-encrypted — and nothing more. A default is shown on every
 * shop that has not written its own, so it may only say what is true of every
 * shop: "free shipping" or "tax included" are the merchant's to claim, never
 * ours.
 *
 * `label` is English and there is deliberately no `en` override, so the
 * customizer's Default tab edits what an English buyer reads. The other nine
 * checkout locales are written out, because a German buyer reading "Secure
 * payment" is exactly what the badges did wrong.
 */
export const DEFAULT_NOTICES: readonly CheckoutNotice[] = [
  {
    id: 'secure',
    section: 'summary',
    icon: 'shield',
    label: 'Secure payment',
    labelTranslations: {
      de: 'Sichere Zahlung',
      fr: 'Paiement sécurisé',
      es: 'Pago seguro',
      it: 'Pagamento sicuro',
      nl: 'Veilig betalen',
      zh: '安全支付',
      'zh-Hant': '安全付款',
      hi: 'सुरक्षित भुगतान',
      ar: 'دفع آمن',
    },
  },
  {
    id: 'ssl',
    section: 'summary',
    icon: 'lock',
    label: 'Encrypted · 256-bit SSL',
    labelTranslations: {
      de: 'Verschlüsselt · 256-Bit-SSL',
      fr: 'Chiffré · SSL 256 bits',
      es: 'Cifrado · SSL de 256 bits',
      it: 'Crittografato · SSL a 256 bit',
      nl: 'Versleuteld · 256-bit SSL',
      zh: '加密 · 256 位 SSL',
      'zh-Hant': '加密 · 256 位元 SSL',
      hi: 'एन्क्रिप्टेड · 256-बिट SSL',
      ar: 'مشفّر · SSL بطول 256 بت',
    },
  },
];

/** A deep copy of `DEFAULT_NOTICES`, safe to edit. */
export function defaultNotices(): CheckoutNotice[] {
  return DEFAULT_NOTICES.map(cloneNotice);
}

export function cloneNotice(notice: CheckoutNotice): CheckoutNotice {
  return { ...notice, labelTranslations: { ...notice.labelTranslations } };
}

/** `#rrggbb` → its three channels, or `null` for anything else. */
function hexChannels(hex: string): [number, number, number] | null {
  const raw = /^#([0-9a-f]{6})$/i.exec(hex.trim())?.[1];
  if (!raw) return null;
  return [
    parseInt(raw.slice(0, 2), 16),
    parseInt(raw.slice(2, 4), 16),
    parseInt(raw.slice(4, 6), 16),
  ];
}

/**
 * Whether a badge colouring is one the dashboard's theme gallery generated.
 *
 * From 2026-09-03 the built-in themes shipped the stock pair recoloured from
 * the theme (control-center `themeBadges`, F-BRAND-24): text = the primary,
 * fill = the surface mixed 8 % towards it, border = mixed 30 %, rounded per
 * channel. Those shops never chose their badge colours either, so they count
 * as stock. The surface is not stored with the badges, so it is recovered:
 * each channel is searched for a surface value that reproduces BOTH the fill
 * and the border — a hand-picked colouring passes that by accident only when
 * the merchant happened to type the exact generated hexes.
 *
 * Frozen on purpose: this recognises what was shipped, so it must not follow
 * any later change to how themes look.
 */
function isThemeTintedBadgeColouring(textColor: string, fill: string, border: string): boolean {
  const primary = hexChannels(textColor);
  const bg = hexChannels(fill);
  const line = hexChannels(border);
  if (!primary || !bg || !line) return false;
  const mixed = (surface: number, to: number, share: number): number =>
    Math.round(surface + (to - surface) * share);
  return primary.every((p, i) => {
    for (let surface = 0; surface <= 255; surface += 1) {
      if (mixed(surface, p, 0.08) === bg[i] && mixed(surface, p, 0.3) === line[i]) return true;
    }
    return false;
  });
}

/**
 * Whether a badge list is a default DeloPay shipped rather than something the
 * merchant configured.
 *
 * Stock means the two stock badges — `secure` / "Secure", then `ssl` /
 * "256-bit SSL" — in a colouring DeloPay generated: the light default, the
 * dark default, or a theme colouring (`isThemeTintedBadgeColouring`, the same
 * one on both badges). Anything else was edited: a changed label, an added,
 * removed or reordered badge, a recolour, and an empty list — a merchant who
 * removed both badges chose to show nothing.
 */
export function isDefaultBadgeSet(badges: readonly TrustBadge[]): boolean {
  if (badges.length !== DEFAULT_BADGES.length) return false;
  const sameContent = badges.every(
    (badge, i) => badge.id === DEFAULT_BADGES[i]?.id && badge.label === DEFAULT_BADGES[i]?.label,
  );
  if (!sameContent) return false;

  const colouring = (set: readonly TrustBadge[]): string =>
    set
      .map((b) => [b.textColor, b.backgroundColor, b.borderColor ?? ''].join('|').toLowerCase())
      .join('/');
  const mine = colouring(badges);
  if (mine === colouring(DEFAULT_BADGES) || mine === colouring(DEFAULT_BADGES_DARK)) return true;

  // A theme gave both badges one colouring, border included.
  const [first] = badges;
  const border = first?.borderColor;
  if (!first || !border) return false;
  const same = (a: string | null, b: string): boolean =>
    (a ?? '').toLowerCase() === b.toLowerCase();
  return (
    badges.every(
      (b) =>
        same(b.textColor, first.textColor) &&
        same(b.backgroundColor, first.backgroundColor) &&
        same(b.borderColor, border),
    ) && isThemeTintedBadgeColouring(first.textColor, first.backgroundColor, border)
  );
}

/**
 * Decide a shop's badges and notices from what it has saved.
 *
 * `null` means the key is absent from what was saved (or unreadable). The
 * rule, in order:
 *
 * 1. Notices saved → they decide; badges are whatever was saved (none if
 *    absent). Every save since notices exist writes the key, so from a shop's
 *    first save on this is the only branch it takes — which is what makes
 *    "removed every notice" stick instead of re-migrating to the defaults.
 * 2. No notices, and no badges or the stock badges → the badges go and the
 *    default notices come in. This is every shop that never touched its trust
 *    row.
 * 3. No notices, and badges the merchant configured → they stay exactly as
 *    they are, and no notices are added: a shop that designed its own trust
 *    row keeps it, and one that removed it keeps it removed.
 *
 * Decode-time only. Nothing is written until the merchant saves.
 */
export function resolveNoticesAndBadges(
  savedBadges: TrustBadge[] | null,
  savedNotices: CheckoutNotice[] | null,
): { trustBadges: TrustBadge[]; notices: CheckoutNotice[] } {
  if (savedNotices !== null) {
    return { trustBadges: savedBadges ?? [], notices: savedNotices };
  }
  if (savedBadges === null || isDefaultBadgeSet(savedBadges)) {
    return { trustBadges: [], notices: defaultNotices() };
  }
  return { trustBadges: savedBadges, notices: [] };
}

// --- Method sections: order and captions -------------------------
//
// Two branding fields for the checkout's payment-method groups: the order they
// render in, and the merchant's own name for each. A section's identity is a
// key, and a key with no caption prints as itself.

const METHOD_SECTION_ORDER_KEY = 'methodSectionOrder';
const METHOD_SECTION_LABELS_KEY = 'methodSectionLabels';

/** Inclusive bounds of one section's order number. */
export const MIN_SECTION_ORDER = 1;
export const MAX_SECTION_ORDER = 100;

/** Ceiling on one caption. */
export const MAX_SECTION_LABEL_LENGTH = 120;

export interface MethodSectionSpec {
  readonly id: string;
  /** `sections.*` translation key, used when uncaptioned. */
  readonly labelKey: string;
  readonly order: number;
}

/**
 * Every known section, with its default number. Lower renders first.
 *
 * Array order is also the tie-break between two sections sharing a number, so
 * it is a display order rather than an arbitrary list.
 */
export const METHOD_SECTIONS: readonly MethodSectionSpec[] = [
  { id: 'card', labelKey: 'sections.card', order: 10 },
  { id: 'wallet', labelKey: 'sections.wallets', order: 20 },
  { id: 'bnpl', labelKey: 'sections.bnpl', order: 30 },
  { id: 'bank_redirect', labelKey: 'sections.onlineBanking', order: 40 },
  { id: 'bank_transfer', labelKey: 'sections.bankTransfer', order: 45 },
  { id: 'crypto', labelKey: 'sections.cryptocurrency', order: 50 },
  { id: 'game_items', labelKey: 'sections.gameItems', order: 60 },
  { id: 'voucher', labelKey: 'sections.vouchers', order: 70 },
  { id: 'gift_card', labelKey: 'sections.giftCards', order: 75 },
  { id: 'reward', labelKey: 'sections.rewards', order: 80 },
  { id: 'cash', labelKey: 'sections.cash', order: 90 },
];

/** Ids that mean an existing section under another name. */
export const SECTION_ALIASES: Readonly<Record<string, string>> = {
  card_redirect: 'card',
};

/** Where an id this list does not know goes: last, not first. */
export const UNKNOWN_SECTION_ORDER = MAX_SECTION_ORDER;

/** Section id → order number. */
export type MethodSectionOrder = Record<string, number>;

/** Section id → locale → the merchant's caption. */
export type MethodSectionLabels = Record<string, Record<string, string>>;

export function canonicalSectionId(category: string): string {
  return SECTION_ALIASES[category] ?? category;
}

export function defaultMethodSectionOrder(): MethodSectionOrder {
  const order: MethodSectionOrder = {};
  for (const section of METHOD_SECTIONS) order[section.id] = section.order;
  return order;
}

/**
 * One order number, validated. `null` for anything that is not an integer in
 * range, so the caller can fall back to the default rather than clamp — a
 * `999` is a mistake, and "last" is a guess at what was meant.
 */
export function validSectionOrder(raw: unknown): number | null {
  if (typeof raw !== 'number' || !Number.isInteger(raw)) return null;
  if (raw < MIN_SECTION_ORDER || raw > MAX_SECTION_ORDER) return null;
  return raw;
}

/**
 * The order from a raw bag value. Total, like `decodeBadges`: anything
 * malformed gives the defaults, one bad number costs only its own section, an
 * unknown id is kept so a newer consumer can place it, and an alias folds onto
 * the section it names.
 */
export function decodeMethodSectionOrder(raw: string | undefined): MethodSectionOrder {
  const order = defaultMethodSectionOrder();
  if (raw === undefined) return order;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return order;
  }
  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) return order;
  for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
    const valid = validSectionOrder(value);
    if (valid === null) continue;
    order[canonicalSectionId(key)] = valid;
  }
  return order;
}

export function encodeMethodSectionOrder(order: MethodSectionOrder): string {
  return JSON.stringify(order);
}

/**
 * Captions from a raw bag value. Tolerant for the same reason as the order,
 * and blank strings are dropped so an emptied field round-trips as absence.
 */
export function decodeMethodSectionLabels(raw: string | undefined): MethodSectionLabels {
  if (raw === undefined) return {};
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return {};
  }
  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
  const labels: MethodSectionLabels = {};
  for (const [section, byLocale] of Object.entries(parsed as Record<string, unknown>)) {
    if (!section || byLocale === null || typeof byLocale !== 'object' || Array.isArray(byLocale)) {
      continue;
    }
    const kept: Record<string, string> = {};
    for (const [locale, text] of Object.entries(byLocale as Record<string, unknown>)) {
      if (typeof text !== 'string') continue;
      const trimmed = text.trim();
      if (!trimmed) continue;
      kept[locale] = trimmed.slice(0, MAX_SECTION_LABEL_LENGTH);
    }
    if (Object.keys(kept).length > 0) labels[canonicalSectionId(section)] = kept;
  }
  return labels;
}

/**
 * Captions for the bag, or `null` when there are none — so an untouched
 * profile's bag is unchanged by this field existing, as `customFields` does.
 */
export function encodeMethodSectionLabels(labels: MethodSectionLabels): string | null {
  const cleaned: MethodSectionLabels = {};
  for (const [section, byLocale] of Object.entries(labels)) {
    const kept: Record<string, string> = {};
    for (const [locale, text] of Object.entries(byLocale)) {
      const trimmed = (text ?? '').trim();
      if (trimmed) kept[locale] = trimmed.slice(0, MAX_SECTION_LABEL_LENGTH);
    }
    if (Object.keys(kept).length > 0) cleaned[section] = kept;
  }
  return Object.keys(cleaned).length > 0 ? JSON.stringify(cleaned) : null;
}

function sectionCatalogueIndex(sectionId: string): number {
  const index = METHOD_SECTIONS.findIndex((section) => section.id === sectionId);
  return index === -1 ? METHOD_SECTIONS.length : index;
}

export function sectionOrderOf(order: MethodSectionOrder, category: string): number {
  return order[canonicalSectionId(category)] ?? UNKNOWN_SECTION_ORDER;
}

/**
 * Compare two section ids: the merchant's number, then this list, then the id.
 *
 * The last two keys are properties of the build rather than of any response,
 * which is what stops two sections on one number swapping between renders.
 */
export function compareSections(order: MethodSectionOrder): (a: string, b: string) => number {
  return (a, b) => {
    const left = canonicalSectionId(a);
    const right = canonicalSectionId(b);
    const byOrder = sectionOrderOf(order, left) - sectionOrderOf(order, right);
    if (byOrder !== 0) return byOrder;
    const byCatalogue = sectionCatalogueIndex(left) - sectionCatalogueIndex(right);
    if (byCatalogue !== 0) return byCatalogue;
    return left < right ? -1 : left > right ? 1 : 0;
  };
}

/**
 * What to print above a section: the merchant's caption for this locale, then
 * their caption in any locale they wrote, then the caller's own name for a
 * section it knows, then the raw id.
 *
 * `translate` must return `undefined` for a key it has no entry for, or step 3
 * swallows the fall-through and prints `sections.<id>`.
 */
export function sectionLabel(
  labels: MethodSectionLabels,
  category: string,
  locale: string,
  knownKey: string | undefined,
  translate: (key: string) => string | undefined,
): string {
  const id = canonicalSectionId(category);
  // `in` rather than `Object.hasOwn`: this package targets a lower `lib` than
  // its consumers, and the map is built by the decoder above.
  if (id in labels) {
    const captions = labels[id] as Record<string, string>;
    // Through `translationFor`, like every other localized branding field: it
    // folds case and walks parent locales, so `de-AT` finds `de`. An exact
    // lookup missed that and fell to the any-caption step below, which returns
    // whichever locale is first in the map — English, for a caption set written
    // `en` then `de`. A German buyer read an English heading.
    // Trimmed: `translationFor` accepts any non-empty string, and a
    // whitespace-only caption is not a caption. The decoder drops those, so
    // this only bites a caller that built the map itself.
    const matched = translationFor(captions, locale)?.trim();
    if (matched) return matched;
    for (const value of Object.values(captions)) {
      const any = value.trim();
      if (any) return any;
    }
  }
  if (knownKey) {
    const translated = translate(knownKey);
    if (translated) return translated;
  }
  return category;
}

/** Accept either the decoded map or the bag's JSON string. */
function parseSectionOrderLoose(raw: unknown): MethodSectionOrder {
  if (typeof raw === 'string') return decodeMethodSectionOrder(raw);
  if (raw !== null && typeof raw === 'object' && !Array.isArray(raw)) {
    return decodeMethodSectionOrder(JSON.stringify(raw));
  }
  return defaultMethodSectionOrder();
}

/** Same, for captions. */
function parseSectionLabelsLoose(raw: unknown): MethodSectionLabels {
  if (typeof raw === 'string') return decodeMethodSectionLabels(raw);
  if (raw !== null && typeof raw === 'object' && !Array.isArray(raw)) {
    return decodeMethodSectionLabels(JSON.stringify(raw));
  }
  return {};
}

/** The `sections.*` key for a known id, or `undefined`. */
export function knownSectionLabelKey(category: string): string | undefined {
  const id = canonicalSectionId(category);
  return METHOD_SECTIONS.find((section) => section.id === id)?.labelKey;
}

const DEFAULT_BRANDING_BASE: Omit<
  CheckoutBranding,
  | keyof typeof LIGHT_PALETTE
  | 'trustBadges'
  | 'notices'
  | 'customFields'
  | 'methodSectionOrder'
  | 'methodSectionLabels'
> = {
  displayName: '',
  logoUrl: '',
  tagline: '',
  // Logo + summary gradient OFF by default — an unconfigured DeloPay
  // checkout reads cleaner without a placeholder logo block, and the
  // gradient implies a primary tint the merchant hasn't yet picked.
  showLogo: false,
  logoShape: 'rounded',
  logoSize: 'md',

  fontFamily: 'inter',
  headingWeight: 'semibold',

  radiusSurface: 'medium',
  radiusInput: 'medium',
  radiusButton: 'medium',
  radiusBadge: 'pill',
  surfaceStyle: 'elevated',
  surfacePadding: 'comfortable',
  verticalGap: 'comfortable',
  inputSize: 'md',
  buttonSize: 'md',

  // Disclosure on by default: with provider-scoped pricing the tile is where
  // the buyer chooses between prices, and a price they cannot see is one they
  // find out about on the statement.
  showSurcharge: true,
  surchargePosition: 'trailing',
  // The button is the surface a buyer recognises fastest; it leads its section
  // unless the merchant says otherwise.
  nativeElementPosition: 'top',
  // Side by side — see the field.
  nativeElementStacked: false,
  surchargeStyle: 'badge',
  surchargeSize: 'sm',
  surchargeShape: 'pill',
  surchargeBorderWidth: 'thin',
  surchargeBorderStyle: 'solid',
  surchargeWeight: 'medium',
  surchargeSign: 'always',
  surchargeUppercase: false,
  // Named, not bare. A tile that reads "-EUR 2.50" beside a brand name is a
  // number with no subject — buyers read it as the price, not as a change to
  // it. "Discount -EUR 2.50" needs no interpreting, and the word is what makes
  // the default configuration legible without the merchant touching anything.
  surchargeLabel: 'labelled',
  // The two directions default differently on purpose, which is the whole
  // reason they are two settings. A fee is something a buyer acts on in money,
  // so it leads with money and says nothing else. A saving is something worth
  // advertising, and the share is the persuasive half of it — "-5.1% (EUR
  // 2.50)" sells what "-EUR 2.50" merely states, and the amount is still right
  // there beside it.
  surchargeFigure: 'amount',
  discountFigure: 'percentThenAmount',
  // Empty: the checkout's own localized words are the default, in every
  // language it ships. A merchant fills these in only to say it differently.
  surchargeWordTranslations: {},
  discountWordTranslations: {},
  // amber-700 / emerald-700, not the 600s: the `plain` style renders the
  // disclosure as coloured TEXT, so it is bound by `WCAG_AA_TEXT` (4.5) rather
  // than by the 3:1 a coloured shape would be, and the 600s come to 3.19 and
  // 3.77 against the default white surface. These are 5.02 and 5.48, pinned by
  // tests. Far enough apart to read as opposite news at a glance rather than as
  // two shades of "extra".
  surchargeColor: '#b45309',
  discountColor: '#047857',

  layout: 'split',
  summaryPosition: 'left',
  showOrderSummary: true,
  summaryGradient: false,
  showTotal: true,
  totalLabel: 'Total',
  showCurrencyCode: false,
  showOrderItems: true,
  cancelLinkEnabled: true,
  cancelLinkPosition: 'top',
  cancelLinkStyle: 'link',

  // Each flat field keeps its meaning: what the merchant wrote, used in every
  // locale that carries no override. Empty maps therefore change nothing for a
  // profile saved before translations existed.
  headerText: '',
  headerTextTranslations: {},
  payButtonLabel: '',
  payButtonLabelTranslations: {},
  cardTermsMessage: '',
  cardTermsMessageTranslations: {},
  footerText: '',
  footerTextTranslations: {},
  supportEmail: '',

  paymentLayout: 'tabs',
  labelStyle: 'above',
  showPoweredBy: true,

  // Every one of these defaults to what the Airwallex pane already did before
  // the section existed, so a merchant who never opens it sees no change:
  // empty preset (defer to the connector account), an outlined box, 16px /
  // 500 type, no focus ring, the browser's own autofill highlight, no valid
  // tint, no marks. The single deliberate change of behaviour is not here —
  // it is the fallback at the END of `resolveAirwallexCardPreset`, which
  // moves a shop that configured NOTHING from the combined row to the
  // labelled split form.
  airwallexCardPreset: '',
  // Off. With this on, no buyer can pay by card on the shop.
  airwallexHideCard: false,
  // Off: the section is named, like every other section on the surface.
  airwallexHideCardTitle: false,
  airwallexFieldChrome: 'outlined',
  airwallexLabelStyle: '',
  airwallexFieldFontSize: 'md',
  airwallexFieldWeight: 'medium',
  airwallexFocusRing: 'none',
  airwallexAutofill: 'system',
  airwallexValidTint: false,
  airwallexNetworkMarks: false,

  // Every card-form field below starts on what its pane drew before the
  // setting existed, so a merchant who never opens a section sees no change.
  stripeTheme: 'auto',
  stripeInputs: 'spaced',
  stripeLabels: 'above',
  stripeSpacing: 'comfortable',
  stripeFieldFontSize: 'md',
  stripeFieldWeight: 'medium',
  stripeFocusRing: 'strong',
  stripeDisableAnimations: false,
  paypalCardLayout: 'split',
  paypalLabelStyle: 'above',
  paypalFieldChrome: 'outlined',
  paypalFieldFontSize: 'md',
  paypalFieldWeight: 'regular',
  paypalFocusRing: 'strong',
  paypalValidTint: false,
  nomupayFormStyle: 'plain',
  nomupayShowLabels: true,
  nomupayShowPlaceholders: true,
  nomupayShowCvvHint: false,
  nomupayFieldChrome: 'outlined',
  nomupayFieldFontSize: 'md',
  nomupayFocusRing: 'none',
  nomupayApplePayButtonStyle: 'auto',
  nomupayApplePayButtonType: 'pay',
  nomupayGooglePayButtonColor: 'auto',
  nomupayGooglePayButtonType: 'pay',

  customCss: '',
};

export const DEFAULT_BRANDING: CheckoutBranding = {
  ...DEFAULT_BRANDING_BASE,
  ...LIGHT_PALETTE,
  // No badges by default any more — notices took their place.
  // `DEFAULT_BADGES` stays exported because `isDefaultBadgeSet` still has to
  // recognise the pair on shops saved before.
  trustBadges: [],
  notices: defaultNotices(),
  customFields: [],
  methodSectionOrder: defaultMethodSectionOrder(),
  methodSectionLabels: {},
};

// Dark counterpart to DEFAULT_BRANDING. Same form-only fields stay empty
// (logo / display name / tagline / copy) — only visual tokens diverge.
// Palette mirrors the design-guidelines dark ramp:
//   - background = slate-950, surface = slate-900 → soft contrast.
//   - text = slate-200, heading = slate-50 → AA+ contrast on the surface.
//   - muted = slate-400, border = slate-800 → chrome that fades into the bg.
//   - accentText = primary-400, button bg = brand blue → brand pops on dark
//     where the light theme's near-black navy CTA would disappear.
//   - surfaceStyle = 'flat' because soft drop-shadows don't read on dark
//     surfaces; the existing 'elevated' shadow tokens are tuned for light.
export const DEFAULT_BRANDING_DARK: CheckoutBranding = {
  ...DEFAULT_BRANDING_BASE,
  primary: '#1E4FEB',
  background: '#020617',
  surface: '#0f172a',
  text: '#e2e8f0',
  heading: '#f8fafc',
  muted: '#94a3b8',
  border: '#1e293b',
  accentText: '#60a5fa',
  buttonBackground: '#1E4FEB',
  buttonText: '#ffffff',
  surfaceStyle: 'flat',
  // amber-400 / emerald-400: the 700s above are far too dark to read on
  // slate-900, and this pair is the same news in the same order at the light
  // end of the ramp — 10.69 and 9.29 against the dark surface, also pinned.
  surchargeColor: '#fbbf24',
  discountColor: '#34d399',
  trustBadges: [],
  notices: defaultNotices(),
  customFields: [],
  methodSectionOrder: defaultMethodSectionOrder(),
  methodSectionLabels: {},
};

// Back-compat alias. Prefer `DEFAULT_BRANDING` directly.
export function defaultBranding(): CheckoutBranding {
  return cloneBranding(DEFAULT_BRANDING);
}

// Deep-ish clone — needed because the form mutates state reactively and we
// don't want shared TrustBadge / CheckoutCustomField object refs across the
// loaded/edit/default snapshots. Spread is shallow; array elements would
// otherwise be shared between snapshots, breaking reset.
export function cloneBranding(b: CheckoutBranding): CheckoutBranding {
  return {
    ...b,
    trustBadges: b.trustBadges.map((badge) => ({ ...badge })),
    notices: b.notices.map(cloneNotice),
    customFields: b.customFields.map(cloneCustomField),
    methodSectionOrder: { ...b.methodSectionOrder },
    methodSectionLabels: Object.fromEntries(
      Object.entries(b.methodSectionLabels).map(([id, byLocale]) => [id, { ...byLocale }]),
    ),
    // Copied, not shared. A spread alone would leave the clone pointing at the
    // original's maps, and the dashboard edits a clone precisely so the form
    // can be abandoned without touching what is saved.
    surchargeWordTranslations: { ...b.surchargeWordTranslations },
    discountWordTranslations: { ...b.discountWordTranslations },
    headerTextTranslations: { ...b.headerTextTranslations },
    payButtonLabelTranslations: { ...b.payButtonLabelTranslations },
    cardTermsMessageTranslations: { ...b.cardTermsMessageTranslations },
    footerTextTranslations: { ...b.footerTextTranslations },
  };
}

export function cloneCustomField(f: CheckoutCustomField): CheckoutCustomField {
  return {
    ...f,
    labelTranslations: { ...f.labelTranslations },
    placeholderTranslations: { ...f.placeholderTranslations },
    helpTextTranslations: { ...f.helpTextTranslations },
    options: f.options.map((o) => ({ ...o, labelTranslations: { ...o.labelTranslations } })),
    visibility: {
      ...f.visibility,
      conditions: f.visibility.conditions.map((c) => ({ ...c })),
    },
  };
}

// --- Sanitizer ----------------------------------------------------------

// Hard cap on the persisted custom CSS payload. Mirrors the editor's
// validation; oversized input is dropped to null rather than truncated,
// since a half-truncated rule is worse than no rule.
export const CUSTOM_CSS_MAX_LENGTH = 50_000;

// Strip CSS tokens that turn a stylesheet into a delivery vector before
// handing the merchant's input to the renderer. The list isn't exhaustive
// — it's the everyday surface area:
//
//   - `</style` would let injected text break out of the <style> block and
//     parse as HTML/JS. Replaced with a benign token (not removed) so
//     `</styled-tag` (unlikely but possible inside a content: string)
//     doesn't silently merge into surrounding text.
//   - `@import` would let the merchant pull in a remote stylesheet on
//     every buyer page-load (analytics by side-channel; possible buyer-IP
//     leak).
//   - `expression(…)` was a legacy IE construct that ran JS from CSS.
//   - `behavior:` and `-moz-binding:` ditto for IE/old-Gecko.
//   - `javascript:` URLs in url(...) execute on resource load in some
//     browsers/contexts.
//
// Anything else passes through. The merchant can still write whatever
// declarations they like (background images, gradients, transforms,
// keyframes, container queries, …).
export function sanitizeCustomCss(raw: string | null | undefined): string | null {
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (trimmed.length === 0) return null;
  if (trimmed.length > CUSTOM_CSS_MAX_LENGTH) return null;

  let out = trimmed;
  out = out.replace(/<\/style/gi, '<\\/style');
  out = out.replace(/@import\b[^;]*;?/gi, '');
  out = out.replace(/expression\s*\(/gi, '/* expression( */');
  out = out.replace(/(^|[^a-z-])behavior\s*:/gi, '$1/* behavior: */');
  out = out.replace(/-moz-binding\s*:/gi, '/* -moz-binding: */');
  out = out.replace(/url\s*\(\s*["']?\s*javascript:/gi, 'url(invalid:');
  return out;
}

// --- Codec --------------------------------------------------------------

// Structural shape the decoder reads. Both `CheckoutDetails` (public buyer
// payload) and `BusinessPaymentLinkConfig` (merchant config request shape)
// satisfy this via duck typing — they each fill some of these and leave the
// rest undefined. `merchant_*` and `seller_*`/`logo` are aliases for the
// same API field, surfaced under different names by the two payloads.
export interface BrandingSource {
  // Identity (one or the other, depending on payload shape)
  merchant_name?: string | null;
  seller_name?: string | null;
  merchant_logo?: string | null;
  logo?: string | null;
  merchant_description?: string | null;

  // Visual
  theme?: string | null;
  background_colour?: string | null;
  payment_button_colour?: string | null;
  payment_button_text_colour?: string | null;

  // Copy
  payment_form_header_text?: string | null;
  payment_button_text?: string | null;
  custom_message_for_card_terms?: string | null;

  // Footer
  branding_visibility?: boolean | null;

  // Stripe
  sdk_layout?: string | null;
  payment_form_label_type?: string | null;

  // The bag for everything else
  sdk_ui_rules?: Record<string, Record<string, string> | null | undefined> | null;
}

const BRANDING_GROUP_KEY = 'branding';

const ALL_FONT_FAMILIES: readonly FontFamily[] = [
  'inter',
  'system',
  'serif',
  'mono',
  'roboto',
  'poppins',
  'manrope',
  'dm-sans',
  'space-grotesk',
  'plex-sans',
  'work-sans',
  'open-sans',
  'lora',
  'playfair',
  'plex-mono',
  'jetbrains-mono',
];

const ALL_RADII: readonly CornerRadius[] = ['square', 'small', 'medium', 'large', 'pill'];
const NON_PILL_RADII: readonly NonPillRadius[] = ['square', 'small', 'medium', 'large'];

function pickEnum<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  if (typeof value !== 'string') return fallback;
  return (allowed as readonly string[]).includes(value) ? (value as T) : fallback;
}

/**
 * `pickEnum` for a field whose "unset" state is the empty string.
 *
 * Anything not on the list — absent, blank, or a token this build has not
 * heard of — decodes to `''`, which every consumer reads as "defer". That is
 * the difference between this and `pickEnum`: there the unknown becomes a
 * concrete value and starts overriding whatever it was deferring to.
 */
function pickEnumOrEmpty<T extends string>(value: unknown, allowed: readonly T[]): T | '' {
  if (typeof value !== 'string') return '';
  return (allowed as readonly string[]).includes(value) ? (value as T) : '';
}

function parseBool(value: unknown, fallback: boolean): boolean {
  if (typeof value === 'boolean') return value;
  if (value === 'true') return true;
  if (value === 'false') return false;
  return fallback;
}

function s(value: string | null | undefined): string {
  return typeof value === 'string' ? value : '';
}

// Decode the JSON-encoded trust badges stored under
// `sdk_ui_rules.branding.trustBadges`. Tolerant: a malformed payload,
// missing fields, or wrong types fall through to null so the caller can
// substitute the default badge set. An explicit empty array means "no
// badges" (we honor it — merchants who hide the row need a way to express
// that).
export function decodeBadges(raw: string | undefined): TrustBadge[] | null {
  if (raw === undefined) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    return parsed
      .filter((b): b is Record<string, unknown> => !!b && typeof b === 'object')
      .map((b, i) => ({
        id: typeof b['id'] === 'string' && b['id'] ? (b['id'] as string) : `badge-${i}`,
        label: typeof b['label'] === 'string' ? (b['label'] as string) : '',
        textColor: typeof b['textColor'] === 'string' ? (b['textColor'] as string) : '#0f172a',
        backgroundColor:
          typeof b['backgroundColor'] === 'string' ? (b['backgroundColor'] as string) : '#f1f5f9',
        borderColor:
          typeof b['borderColor'] === 'string' && b['borderColor']
            ? (b['borderColor'] as string)
            : null,
      }))
      .filter((b) => b.label.length > 0);
  } catch {
    return null;
  }
}

export function encodeBadges(badges: TrustBadge[]): string {
  return JSON.stringify(
    badges.map((b) => ({
      id: b.id,
      label: b.label,
      textColor: b.textColor,
      backgroundColor: b.backgroundColor,
      ...(b.borderColor ? { borderColor: b.borderColor } : {}),
    })),
  );
}

// --- Notices codec --------------------------------------------

/** A notice id is code-shaped: it keys a drag, never a buyer's eyes. */
const NOTICE_ID_MAX_LENGTH = 40;

function capNoticeText(value: string): string {
  return Array.from(value).slice(0, NOTICE_LABEL_MAX_LENGTH).join('');
}

/**
 * Normalize a list of notices from any source — the bag, an import file.
 *
 * Total, like `decodeBadges`: every buyer sees these, so a malformed entry
 * degrades to a smaller list, never to a broken page.
 *
 * - not an object → dropped;
 * - unknown section → `summary`; unknown icon → `NOTICE_ICON_FALLBACK`;
 * - id cleaned to `[A-Za-z0-9_-]`, a missing or repeated one replaced, so the
 *   editor can track every row across a drag;
 * - text cut to `NOTICE_LABEL_MAX_LENGTH` characters, blank overrides dropped;
 * - a notice with no text in any language → dropped (it would draw an icon
 *   with nothing beside it);
 * - past `NOTICES_PER_SECTION` in its section → dropped, first come first
 *   kept, so what is stored is what is drawn.
 */
function normalizeNotices(list: readonly unknown[]): CheckoutNotice[] {
  const seen = new Set<string>();
  const perSection = new Map<NoticeSection, number>();
  const out: CheckoutNotice[] = [];

  list.forEach((raw, index) => {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return;
    const src = raw as Record<string, unknown>;

    const label = typeof src['label'] === 'string' ? capNoticeText(src['label']) : '';
    const labelTranslations: CustomFieldTranslations = {};
    for (const [locale, text] of Object.entries(parseTranslations(src['labelTranslations']))) {
      labelTranslations[locale] = capNoticeText(text);
    }
    if (label.trim().length === 0 && Object.keys(labelTranslations).length === 0) return;

    const section = pickEnum<NoticeSection>(src['section'], NOTICE_SECTIONS, 'summary');
    const used = perSection.get(section) ?? 0;
    if (used >= NOTICES_PER_SECTION) return;
    perSection.set(section, used + 1);

    let id =
      typeof src['id'] === 'string'
        ? src['id'].replace(/[^A-Za-z0-9_-]/g, '').slice(0, NOTICE_ID_MAX_LENGTH)
        : '';
    if (!id || seen.has(id)) id = `notice-${index + 1}`;
    while (seen.has(id)) id = `${id}x`;
    seen.add(id);

    out.push({
      id,
      section,
      icon: isNoticeIcon(src['icon']) ? src['icon'] : NOTICE_ICON_FALLBACK,
      label,
      labelTranslations,
    });
  });

  return out;
}

/**
 * Decode `sdk_ui_rules.branding.notices`.
 *
 * `null` when the key is absent or not a JSON array — "this shop has no say
 * about notices", which `resolveNoticesAndBadges` turns into the migration.
 * An empty array is a say: the merchant removed every notice.
 */
export function decodeNotices(raw: string | undefined): CheckoutNotice[] | null {
  if (raw === undefined) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  return Array.isArray(parsed) ? normalizeNotices(parsed) : null;
}

/** The import file's form: an array inline rather than a JSON string. */
export function parseNoticesLoose(raw: unknown): CheckoutNotice[] | null {
  if (typeof raw === 'string') return decodeNotices(raw);
  return Array.isArray(raw) ? normalizeNotices(raw) : null;
}

/**
 * Encode notices for the bag. Always a JSON array, `[]` included: the key's
 * presence is what tells a later decode that the shop has decided, so it must
 * be written even when there is nothing in it.
 */
export function encodeNotices(notices: readonly CheckoutNotice[]): string {
  return JSON.stringify(
    notices.map((n) => {
      const translations = Object.entries(n.labelTranslations).filter(
        ([, text]) => text.trim().length > 0,
      );
      return {
        id: n.id,
        section: n.section,
        icon: n.icon,
        label: n.label,
        ...(translations.length > 0 ? { labelTranslations: Object.fromEntries(translations) } : {}),
      };
    }),
  );
}

/**
 * A notice's text in the buyer's locale: exact locale, then its base
 * language, then the default text — the order every other piece of merchant
 * copy resolves in (`checkoutCopy`, `customFieldText`).
 *
 * `''` when the notice has no text for this locale: a notice written only in
 * some languages, with no default, is one the merchant shows only in those
 * languages (the POC's "empty means: not in this language"). It is not a
 * notice to draw without words — use `shownNotices`, which leaves it out.
 */
export function noticeText(notice: CheckoutNotice, locale?: string): string {
  return translationFor(notice.labelTranslations, locale) ?? notice.label;
}

/**
 * The notices one section draws, in order, capped at `NOTICES_PER_SECTION`.
 * The decoder already caps; this caps again for state that never went through
 * it, such as the customizer's unsaved edits in the live preview.
 */
export function noticesIn(
  notices: readonly CheckoutNotice[],
  section: NoticeSection,
): CheckoutNotice[] {
  return notices.filter((n) => n.section === section).slice(0, NOTICES_PER_SECTION);
}

/** One notice as a buyer sees it: the notice, and its text in their locale. */
export interface ShownNotice {
  notice: CheckoutNotice;
  text: string;
}

/**
 * What one section draws for a buyer: its notices (capped, as `noticesIn`),
 * each with its text in `locale`, leaving out every notice with no text there.
 *
 * The one place that decides it, so no renderer can draw an icon with nothing
 * beside it. A notice written only in German is simply absent for an English
 * buyer — the cap is applied first, as the decoder applies it, so what a
 * merchant stored in a section is what can ever be drawn from it.
 */
export function shownNotices(
  notices: readonly CheckoutNotice[],
  section: NoticeSection,
  locale?: string,
): ShownNotice[] {
  return noticesIn(notices, section)
    .map((notice) => ({ notice, text: noticeText(notice, locale).trim() }))
    .filter((entry) => entry.text.length > 0);
}

// --- Custom checkout fields codec ---------------------------------------

export const CUSTOM_FIELDS_MAX = 20;
// Metadata keys: start with a letter, then letters/digits/underscore/dash.
// Keeps keys safe for every downstream metadata consumer (dashboards,
// exports, PSP forwarding).
export const CUSTOM_FIELD_KEY_PATTERN = /^[A-Za-z][A-Za-z0-9_-]{0,39}$/;

export const ALL_CUSTOM_FIELD_TYPES: readonly CustomFieldType[] = [
  'text',
  'textarea',
  'password',
  'email',
  'select',
  'checkbox',
];

// The two values a checkbox field ever submits. Kept as strings because the
// whole metadata bag is `Record<string, string>` on the wire.
export const CHECKBOX_CHECKED = 'true';
export const CHECKBOX_UNCHECKED = 'false';

/** Whether a stored/submitted checkbox value counts as ticked. Tolerant of
 *  case and padding so a value round-tripped through a shop integration
 *  ('True', ' true ') still reads correctly. */
export function isCheckboxChecked(value: string | null | undefined): boolean {
  return typeof value === 'string' && value.trim().toLowerCase() === CHECKBOX_CHECKED;
}

/** Types whose value is free text, so length bounds and a placeholder apply.
 *  `select` and `checkbox` are choice controls and have neither. */
export function customFieldIsTextLike(type: CustomFieldType): boolean {
  return type !== 'select' && type !== 'checkbox';
}

// Rule-builder vocabulary. Kept next to the codec so the editor, the
// evaluator and the Rust mirror all read from one list.
export const CUSTOM_FIELD_CONDITIONS_MAX = 10;

export const ALL_CUSTOM_FIELD_CONDITION_SOURCES: readonly CustomFieldConditionSource[] = [
  'metadata',
  'currency',
  'amount',
];

export const ALL_CUSTOM_FIELD_OPERATORS: readonly CustomFieldOperator[] = [
  'equals',
  'not_equals',
  'contains',
  'not_contains',
  'starts_with',
  'ends_with',
  'in',
  'not_in',
  'exists',
  'not_exists',
  'gt',
  'gte',
  'lt',
  'lte',
];

// Which operators make sense per source. Currency is a closed 3-letter set,
// so substring/numeric operators would only ever confuse; amount is numeric,
// so string operators don't apply. Metadata is free-form and gets all of them.
export const CUSTOM_FIELD_OPERATORS_BY_SOURCE: Record<
  CustomFieldConditionSource,
  readonly CustomFieldOperator[]
> = {
  metadata: ALL_CUSTOM_FIELD_OPERATORS,
  currency: ['equals', 'not_equals', 'in', 'not_in'],
  amount: ['equals', 'not_equals', 'gt', 'gte', 'lt', 'lte'],
};

// Operators that ignore the right-hand operand — the editor hides the value
// input for these, and validation must not demand a value.
export const CUSTOM_FIELD_VALUELESS_OPERATORS: readonly CustomFieldOperator[] = [
  'exists',
  'not_exists',
];

export function customFieldOperatorTakesValue(operator: CustomFieldOperator): boolean {
  return !CUSTOM_FIELD_VALUELESS_OPERATORS.includes(operator);
}

// The operator a condition falls back to when its source changes and the
// current operator isn't valid for the new source.
export function defaultOperatorForSource(source: CustomFieldConditionSource): CustomFieldOperator {
  return CUSTOM_FIELD_OPERATORS_BY_SOURCE[source][0] ?? 'equals';
}

export function defaultCustomFieldVisibility(): CustomFieldVisibility {
  return { mode: 'always', match: 'all', conditions: [] };
}

function parseTranslations(raw: unknown): CustomFieldTranslations {
  // The branding bag stores every value as a string, so a map persisted there
  // arrives as JSON; an import file and a custom field carry it inline. Both
  // reach this, and a string that is not JSON is simply not a map.
  let value = raw;
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value) as unknown;
    } catch {
      return {};
    }
  }
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const out: CustomFieldTranslations = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    // Own properties only: a persisted key of `__proto__` or `constructor`
    // must not reach the object being built, and `Object.entries` already
    // gives own enumerable keys only — the length check is what drops a blank
    // override, which means "no override" rather than "render nothing".
    if (typeof v === 'string' && v.trim().length > 0) out[k] = v;
  }
  return out;
}

/**
 * A translations map for the branding bag, or `undefined` when there is
 * nothing to store.
 *
 * Omitting the key rather than writing `{}` keeps the bag of a merchant who
 * never opened the translations strip byte-identical to what it was before
 * these fields existed.
 */
function encodeTranslations(map: CustomFieldTranslations): string | undefined {
  const entries = Object.entries(map).filter(([, v]) => v.trim().length > 0);
  return entries.length > 0 ? JSON.stringify(Object.fromEntries(entries)) : undefined;
}

// Normalize one persisted condition. Total: an unusable source/operator
// falls back rather than dropping the row, so a rule authored by a newer
// control-center never silently becomes "always visible" on an older
// decoder — it becomes a stricter, still-evaluable rule.
function normalizeCondition(raw: unknown, index: number): CustomFieldCondition | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const c = raw as Record<string, unknown>;
  const source = pickEnum<CustomFieldConditionSource>(
    c['source'],
    ALL_CUSTOM_FIELD_CONDITION_SOURCES,
    'metadata',
  );
  const allowed = CUSTOM_FIELD_OPERATORS_BY_SOURCE[source];
  const operator = pickEnum<CustomFieldOperator>(
    c['operator'],
    allowed,
    defaultOperatorForSource(source),
  );
  const rawValue = c['value'];
  return {
    id: typeof c['id'] === 'string' && c['id'] ? (c['id'] as string) : `cond-${index}`,
    source,
    // Only metadata conditions carry a key; drop anything else so the
    // encoded form stays canonical.
    key: source === 'metadata' && typeof c['key'] === 'string' ? c['key'].trim() : '',
    operator,
    value:
      typeof rawValue === 'string'
        ? rawValue
        : typeof rawValue === 'number' || typeof rawValue === 'boolean'
          ? String(rawValue)
          : '',
  };
}

function normalizeVisibility(raw: unknown): CustomFieldVisibility {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return defaultCustomFieldVisibility();
  const v = raw as Record<string, unknown>;
  const conditions: CustomFieldCondition[] = Array.isArray(v['conditions'])
    ? (v['conditions'] as unknown[])
        .slice(0, CUSTOM_FIELD_CONDITIONS_MAX)
        .map(normalizeCondition)
        .filter((c): c is CustomFieldCondition => c !== null)
    : [];
  return {
    mode: pickEnum<'always' | 'match'>(v['mode'], ['always', 'match'], 'always'),
    match: pickEnum<'all' | 'any'>(v['match'], ['all', 'any'], 'all'),
    conditions,
  };
}

function parseBoundedInt(raw: unknown): number | null {
  const n = typeof raw === 'number' ? raw : typeof raw === 'string' ? Number(raw) : NaN;
  if (!Number.isInteger(n) || n < 0) return null;
  return Math.min(n, 5000);
}

// Normalize one persisted/imported field object. Total: anything malformed
// falls back per-property; returns null only when there is no usable key.
function normalizeCustomField(raw: unknown, index: number): CheckoutCustomField | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const f = raw as Record<string, unknown>;
  const key = typeof f['key'] === 'string' ? f['key'].trim() : '';
  if (!CUSTOM_FIELD_KEY_PATTERN.test(key)) return null;

  const type = pickEnum<CustomFieldType>(f['type'], ALL_CUSTOM_FIELD_TYPES, 'text');

  const options: CustomFieldOption[] =
    type === 'select' && Array.isArray(f['options'])
      ? (f['options'] as unknown[])
          .filter((o): o is Record<string, unknown> => !!o && typeof o === 'object')
          .map((o) => {
            const value = typeof o['value'] === 'string' ? o['value'] : '';
            return {
              value,
              label: typeof o['label'] === 'string' && o['label'] ? (o['label'] as string) : value,
              labelTranslations: parseTranslations(o['labelTranslations']),
            };
          })
          .filter((o) => o.value.length > 0)
      : [];

  // Length bounds are meaningless for choice controls; drop them at decode
  // so a field switched to checkbox/select can't carry stale bounds that
  // would then be validated against 'true'/'false'.
  const textLike = customFieldIsTextLike(type);
  const minLength = textLike ? parseBoundedInt(f['minLength']) : null;
  const maxLength = textLike ? parseBoundedInt(f['maxLength']) : null;

  // A checkbox default is a tick state, not free text: anything that isn't
  // truthy normalizes to unchecked, so the control can never start in a
  // third state.
  const rawDefault = typeof f['defaultValue'] === 'string' ? f['defaultValue'] : '';
  const defaultValue =
    type === 'checkbox'
      ? isCheckboxChecked(rawDefault)
        ? CHECKBOX_CHECKED
        : CHECKBOX_UNCHECKED
      : rawDefault;

  return {
    id: typeof f['id'] === 'string' && f['id'] ? (f['id'] as string) : `field-${index}`,
    key,
    type,
    label: typeof f['label'] === 'string' && f['label'] ? (f['label'] as string) : key,
    labelTranslations: parseTranslations(f['labelTranslations']),
    placeholder: typeof f['placeholder'] === 'string' ? (f['placeholder'] as string) : '',
    placeholderTranslations: parseTranslations(f['placeholderTranslations']),
    helpText: typeof f['helpText'] === 'string' ? (f['helpText'] as string) : '',
    helpTextTranslations: parseTranslations(f['helpTextTranslations']),
    required: parseBool(f['required'], false),
    enabled: parseBool(f['enabled'], true),
    minLength,
    // Guard inverted bounds at decode so consumers never see min > max.
    maxLength: maxLength !== null && minLength !== null && maxLength < minLength ? null : maxLength,
    defaultValue,
    options,
    visibility: normalizeVisibility(f['visibility']),
  };
}

// Parse an array of field objects (already JSON-parsed). Null when the
// input isn't an array — callers fall back to "no custom fields". Duplicate
// keys keep the first occurrence; the list is capped at CUSTOM_FIELDS_MAX.
export function parseCustomFieldsLoose(raw: unknown): CheckoutCustomField[] | null {
  if (!Array.isArray(raw)) return null;
  const seen = new Set<string>();
  const out: CheckoutCustomField[] = [];
  for (let i = 0; i < raw.length && out.length < CUSTOM_FIELDS_MAX; i++) {
    const field = normalizeCustomField(raw[i], i);
    if (!field || seen.has(field.key)) continue;
    seen.add(field.key);
    out.push(field);
  }
  return out;
}

// Decode the JSON-encoded custom fields stored under
// `sdk_ui_rules.branding.customFields`. Tolerant like `decodeBadges`:
// malformed payloads fall through to null.
export function decodeCustomFields(raw: string | undefined): CheckoutCustomField[] | null {
  if (raw === undefined) return null;
  try {
    return parseCustomFieldsLoose(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function encodeCustomFields(fields: CheckoutCustomField[]): string {
  const nonEmpty = (m: CustomFieldTranslations): CustomFieldTranslations | undefined => {
    const entries = Object.entries(m).filter(([, v]) => v.trim().length > 0);
    return entries.length > 0 ? Object.fromEntries(entries) : undefined;
  };
  return JSON.stringify(
    fields.map((f) => ({
      id: f.id,
      key: f.key,
      type: f.type,
      label: f.label,
      ...(nonEmpty(f.labelTranslations)
        ? { labelTranslations: nonEmpty(f.labelTranslations) }
        : {}),
      ...(f.placeholder ? { placeholder: f.placeholder } : {}),
      ...(nonEmpty(f.placeholderTranslations)
        ? { placeholderTranslations: nonEmpty(f.placeholderTranslations) }
        : {}),
      ...(f.helpText ? { helpText: f.helpText } : {}),
      ...(nonEmpty(f.helpTextTranslations)
        ? { helpTextTranslations: nonEmpty(f.helpTextTranslations) }
        : {}),
      ...(f.required ? { required: true } : {}),
      ...(f.enabled ? {} : { enabled: false }),
      // Length bounds only exist for free-text types; a choice control that
      // still carries them is stale state the decoder would drop anyway.
      ...(customFieldIsTextLike(f.type) && f.minLength !== null ? { minLength: f.minLength } : {}),
      ...(customFieldIsTextLike(f.type) && f.maxLength !== null ? { maxLength: f.maxLength } : {}),
      // A checkbox persists only "starts ticked"; unticked is the decoder's
      // default, so writing 'false' would be noise on every such field.
      ...(f.type === 'checkbox'
        ? isCheckboxChecked(f.defaultValue)
          ? { defaultValue: CHECKBOX_CHECKED }
          : {}
        : f.defaultValue
          ? { defaultValue: f.defaultValue }
          : {}),
      ...(f.type === 'select' ? { options: f.options } : {}),
      // Omitted for unconditional fields so the stored blob (and every
      // pre-feature payload) stays byte-identical to what it was.
      ...(f.visibility.mode === 'match' ? { visibility: encodeVisibility(f.visibility) } : {}),
    })),
  );
}

function encodeVisibility(v: CustomFieldVisibility): Record<string, unknown> {
  return {
    mode: v.mode,
    match: v.match,
    conditions: v.conditions.map((c) => ({
      id: c.id,
      source: c.source,
      ...(c.source === 'metadata' && c.key ? { key: c.key } : {}),
      operator: c.operator,
      ...(customFieldOperatorTakesValue(c.operator) && c.value ? { value: c.value } : {}),
    })),
  };
}

// --- Conditional-visibility evaluator -----------------------------------

/** Facts a rule can read. `amount` is in the currency's minor unit (what
 *  the payment intent stores); `metadata` values are already flattened to
 *  strings by `customFieldContextFromMetadata`. */
export interface CustomFieldContext {
  amount: number;
  currency: string;
  metadata: Record<string, string>;
}

/** Flatten a payment's raw `metadata` object into the string map a rule
 *  compares against. Arrays join on `,` so a list-shaped value stays usable
 *  with `contains` / `in`; objects fall back to JSON. Mirrors the Rust
 *  side's `flatten_metadata`. */
export function customFieldContextFromMetadata(raw: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return out;
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    const flat = flattenMetadataValue(value);
    if (flat !== null) out[key] = flat;
  }
  return out;
}

function flattenMetadataValue(value: unknown): string | null {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (value === null || value === undefined) return null;
  if (Array.isArray(value)) {
    return value
      .map((v) => flattenMetadataValue(v))
      .filter((v): v is string => v !== null)
      .join(',');
  }
  try {
    return JSON.stringify(value);
  } catch {
    return null;
  }
}

// Case- and whitespace-insensitive: merchants type `Windows` in the builder
// and the shop sends `windows`. Exact-case matching would be a support
// ticket generator.
function norm(value: string): string {
  return value.trim().toLowerCase();
}

// Decimal-float grammar, deliberately narrower than `Number()`.
//
// `Number()` accepts radix-prefixed literals — `Number('0x10') === 16` — while
// the router's Rust mirror uses `f64::from_str`, which rejects them. Left
// unconstrained, a metadata value of `0x10` would satisfy `gte 5` in the
// control-center's builder and fail it on the backend: the merchant sees one
// rule, buyers get another. This regex is `f64::from_str`'s number grammar
// (sign, digits with optional point, optional exponent); `inf`/`nan` are
// excluded here rather than by the finiteness check below, which matches
// Rust filtering them out too.
const DECIMAL_NUMBER_PATTERN = /^[+-]?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?$/;

// Both implementations compare as f64, so they round identically. Payment
// amounts in minor units are far below 2^53, where that is lossless.
function numeric(value: string): number | null {
  const trimmed = value.trim();
  if (!DECIMAL_NUMBER_PATTERN.test(trimmed)) return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}

function splitList(value: string): string[] {
  return value
    .split(',')
    .map((part) => norm(part))
    .filter((part) => part.length > 0);
}

/** Resolve the left-hand operand. `undefined` means "the metadata key is
 *  absent" — which only `exists`/`not_exists` distinguish from empty. */
function operandFor(condition: CustomFieldCondition, ctx: CustomFieldContext): string | undefined {
  switch (condition.source) {
    case 'currency':
      return ctx.currency;
    case 'amount':
      return String(ctx.amount);
    case 'metadata':
      return Object.prototype.hasOwnProperty.call(ctx.metadata, condition.key)
        ? ctx.metadata[condition.key]
        : undefined;
  }
}

export function evaluateCustomFieldCondition(
  condition: CustomFieldCondition,
  ctx: CustomFieldContext,
): boolean {
  const raw = operandFor(condition, ctx);
  const actual = raw ?? '';
  const expected = condition.value;

  switch (condition.operator) {
    case 'exists':
      return raw !== undefined && raw.trim().length > 0;
    case 'not_exists':
      return raw === undefined || raw.trim().length === 0;
    case 'equals':
      return norm(actual) === norm(expected);
    case 'not_equals':
      return norm(actual) !== norm(expected);
    case 'contains':
      return norm(actual).includes(norm(expected));
    case 'not_contains':
      return !norm(actual).includes(norm(expected));
    case 'starts_with':
      return norm(actual).startsWith(norm(expected));
    case 'ends_with':
      return norm(actual).endsWith(norm(expected));
    case 'in':
      return splitList(expected).includes(norm(actual));
    case 'not_in':
      return !splitList(expected).includes(norm(actual));
    case 'gt':
    case 'gte':
    case 'lt':
    case 'lte': {
      // Both sides must be numbers. A non-numeric metadata value never
      // matches a numeric comparison (rather than coercing to 0).
      const a = numeric(actual);
      const b = numeric(expected);
      if (a === null || b === null) return false;
      if (condition.operator === 'gt') return a > b;
      if (condition.operator === 'gte') return a >= b;
      if (condition.operator === 'lt') return a < b;
      return a <= b;
    }
  }
}

/**
 * Whether a field's rule matches the payment.
 *
 * Fail-open in two spots, deliberately: `mode: 'always'` and a `match` rule
 * with no conditions both resolve to visible. A half-authored rule should
 * never silently swallow a field the merchant needs collected — the
 * control-center flags the empty rule as a validation issue instead.
 */
export function evaluateCustomFieldVisibility(
  field: CheckoutCustomField,
  ctx: CustomFieldContext,
): boolean {
  const { mode, match, conditions } = field.visibility;
  if (mode !== 'match' || conditions.length === 0) return true;
  return match === 'any'
    ? conditions.some((c) => evaluateCustomFieldCondition(c, ctx))
    : conditions.every((c) => evaluateCustomFieldCondition(c, ctx));
}

/** Filter a field list to what the buyer should see for this payment.
 *  Disabled fields are dropped here too — the two reasons a field doesn't
 *  render are the same to every consumer. */
export function visibleCustomFields(
  fields: CheckoutCustomField[],
  ctx: CustomFieldContext,
): CheckoutCustomField[] {
  return fields.filter((f) => f.enabled && evaluateCustomFieldVisibility(f, ctx));
}

// Exact-locale-then-base-language lookup. Own-property + string checks so
// a hostile or odd locale string ('constructor', '__proto__') can never
// surface a prototype-chain member as a "translation".
/**
 * Look up one locale's override, walking from most to least specific.
 *
 * Two things this does that an exact-then-base lookup did not, both of which
 * became reachable the moment `zh-Hant` joined the locale list:
 *
 * - **Every parent tag, not just the base.** `zh-Hant-TW` now finds a stored
 *   `zh-Hant` before falling through to `zh`. Jumping straight to the base
 *   skipped the script entirely, so a Taiwanese buyer read the merchant's
 *   simplified copy while a `zh-Hant` box sat filled in beside it.
 * - **Case-insensitive keys.** Locale tags are case-insensitive by spec and
 *   arrive both ways — `zh-hant` from a hand-written link, `zh-Hant` from the
 *   dashboard. A case-sensitive miss looks exactly like "no translation".
 *
 * The map's own casing is preserved on write; only the comparison folds.
 */
function translationFor(map: CustomFieldTranslations, locale?: string): string | null {
  if (!locale) return null;

  const folded = new Map<string, string>();
  for (const [k, v] of Object.entries(map)) {
    if (typeof v === 'string' && v.length > 0 && !folded.has(k.toLowerCase())) {
      folded.set(k.toLowerCase(), v);
    }
  }

  const parts = locale.toLowerCase().split('-');
  for (let i = parts.length; i > 0; i -= 1) {
    const hit = folded.get(parts.slice(0, i).join('-'));
    if (hit) return hit;
  }
  return null;
}

// Resolve the display string for a field part in a buyer locale.
// Exact locale wins ('de-AT'), then its base language ('de'), then the
// field's default-language string; labels finally fall back to the key so
// a field is never rendered nameless.
export function customFieldText(
  field: CheckoutCustomField,
  part: 'label' | 'placeholder' | 'helpText',
  locale?: string,
): string {
  const map =
    part === 'label'
      ? field.labelTranslations
      : part === 'placeholder'
        ? field.placeholderTranslations
        : field.helpTextTranslations;
  const translated = translationFor(map, locale);
  if (translated) return translated;
  const fallback = field[part];
  if (fallback) return fallback;
  return part === 'label' ? field.key : '';
}

export function customFieldOptionLabel(option: CustomFieldOption, locale?: string): string {
  return translationFor(option.labelTranslations, locale) ?? (option.label || option.value);
}

// Read a branding source into a fully-resolved CheckoutBranding.
// Flat fields are authoritative when set; the bag fills in everything not
// reachable via flat columns. Persisted `labelStyle: 'floating'` (legacy
// "floating" mock) decodes to `'above'`.
export function decodeBranding(source: BrandingSource | null | undefined): CheckoutBranding {
  if (!source) return cloneBranding(DEFAULT_BRANDING);

  const extras: Record<string, string> = (source.sdk_ui_rules?.[BRANDING_GROUP_KEY] ??
    {}) as Record<string, string>;

  // Badges and notices are decided together: which one a shop gets depends
  // on what it saved of both.
  const trustRow = resolveNoticesAndBadges(
    decodeBadges(extras['trustBadges']),
    decodeNotices(extras['notices']),
  );
  const decodedCustomFields = decodeCustomFields(extras['customFields']);

  // Identity name: public payloads call it `merchant_name`, request payloads
  // call it `seller_name`. Same API field; pick whichever is present.
  const displayName = s(source.merchant_name) || s(source.seller_name);
  const logoUrl = s(source.merchant_logo) || s(source.logo);

  // Tagline lives in the bag, but `merchant_description` is its public
  // surface. Either may fill it; bag wins if both are set.
  const tagline = s(extras['tagline']) || s(source.merchant_description);

  // Stripe label style: the bag's `labelStyle` is the new source of truth;
  // the legacy flat `payment_form_label_type` is consulted only when the
  // bag is empty. `'floating'` (a legacy preview-only value that Stripe
  // Elements never honored) decodes to `'above'`. `'never'` (legacy API
  // wording) decodes to `'hidden'`.
  const labelStyle: LabelStyle = (() => {
    const fromBag = extras['labelStyle'];
    if (fromBag === 'above' || fromBag === 'hidden') return fromBag;
    if (fromBag === 'floating') return 'above';
    const legacy = source.payment_form_label_type;
    if (legacy === 'above' || legacy === 'floating') return 'above';
    if (legacy === 'hidden' || legacy === 'never') return 'hidden';
    return DEFAULT_BRANDING.labelStyle;
  })();

  return {
    displayName,
    logoUrl,
    tagline,
    showLogo: parseBool(extras['showLogo'], DEFAULT_BRANDING.showLogo),
    logoShape: pickEnum<LogoShape>(
      extras['logoShape'],
      ['square', 'rounded', 'circle'],
      DEFAULT_BRANDING.logoShape,
    ),
    logoSize: pickEnum<LogoSize>(extras['logoSize'], ['sm', 'md', 'lg'], DEFAULT_BRANDING.logoSize),

    primary: s(source.theme) || DEFAULT_BRANDING.primary,
    background: s(source.background_colour) || DEFAULT_BRANDING.background,
    surface: s(extras['surface']) || DEFAULT_BRANDING.surface,
    text: s(extras['text']) || DEFAULT_BRANDING.text,
    heading: s(extras['heading']) || s(extras['text']) || DEFAULT_BRANDING.heading,
    muted: s(extras['muted']) || DEFAULT_BRANDING.muted,
    border: s(extras['border']) || DEFAULT_BRANDING.border,
    accentText: s(extras['accentText']) || s(source.theme) || DEFAULT_BRANDING.accentText,
    buttonBackground:
      s(source.payment_button_colour) || s(source.theme) || DEFAULT_BRANDING.buttonBackground,
    buttonText: s(source.payment_button_text_colour) || DEFAULT_BRANDING.buttonText,

    fontFamily: pickEnum<FontFamily>(
      extras['fontFamily'],
      ALL_FONT_FAMILIES,
      DEFAULT_BRANDING.fontFamily,
    ),
    headingWeight: pickEnum<FontWeight>(
      extras['headingWeight'],
      ['regular', 'medium', 'semibold', 'bold'],
      DEFAULT_BRANDING.headingWeight,
    ),

    radiusSurface: pickEnum<NonPillRadius>(
      extras['radiusSurface'],
      NON_PILL_RADII,
      DEFAULT_BRANDING.radiusSurface,
    ),
    radiusInput: pickEnum<NonPillRadius>(
      extras['radiusInput'],
      NON_PILL_RADII,
      DEFAULT_BRANDING.radiusInput,
    ),
    radiusButton: pickEnum<CornerRadius>(
      extras['radiusButton'],
      ALL_RADII,
      DEFAULT_BRANDING.radiusButton,
    ),
    radiusBadge: pickEnum<CornerRadius>(
      extras['radiusBadge'],
      ALL_RADII,
      DEFAULT_BRANDING.radiusBadge,
    ),
    surfaceStyle: pickEnum<SurfaceStyle>(
      extras['surfaceStyle'],
      ['flat', 'outlined', 'elevated'],
      DEFAULT_BRANDING.surfaceStyle,
    ),
    surfacePadding: pickEnum<SpacingScale>(
      extras['surfacePadding'],
      ['compact', 'comfortable', 'spacious'],
      DEFAULT_BRANDING.surfacePadding,
    ),
    verticalGap: pickEnum<SpacingScale>(
      extras['verticalGap'],
      ['compact', 'comfortable', 'spacious'],
      DEFAULT_BRANDING.verticalGap,
    ),
    inputSize: pickEnum<SizeScale>(
      extras['inputSize'],
      ['sm', 'md', 'lg'],
      DEFAULT_BRANDING.inputSize,
    ),
    buttonSize: pickEnum<SizeScale>(
      extras['buttonSize'],
      ['sm', 'md', 'lg'],
      DEFAULT_BRANDING.buttonSize,
    ),

    showSurcharge: parseBool(extras['showSurcharge'], DEFAULT_BRANDING.showSurcharge),
    surchargePosition: pickEnum<SurchargePosition>(
      extras['surchargePosition'],
      SURCHARGE_POSITIONS,
      DEFAULT_BRANDING.surchargePosition,
    ),
    nativeElementStacked: parseBool(
      extras['nativeElementStacked'],
      DEFAULT_BRANDING.nativeElementStacked,
    ),
    nativeElementPosition: pickEnum<NativeElementPosition>(
      extras['nativeElementPosition'],
      NATIVE_ELEMENT_POSITIONS,
      DEFAULT_BRANDING.nativeElementPosition,
    ),
    surchargeStyle: pickEnum<SurchargeStyle>(
      extras['surchargeStyle'],
      SURCHARGE_STYLES,
      DEFAULT_BRANDING.surchargeStyle,
    ),
    surchargeSize: pickEnum<SurchargeSize>(
      extras['surchargeSize'],
      SURCHARGE_SIZES,
      DEFAULT_BRANDING.surchargeSize,
    ),
    surchargeShape: pickEnum<SurchargeShape>(
      extras['surchargeShape'],
      SURCHARGE_SHAPES,
      DEFAULT_BRANDING.surchargeShape,
    ),
    surchargeBorderWidth: pickEnum<SurchargeBorderWidth>(
      extras['surchargeBorderWidth'],
      SURCHARGE_BORDER_WIDTHS,
      DEFAULT_BRANDING.surchargeBorderWidth,
    ),
    surchargeBorderStyle: pickEnum<SurchargeBorderStyle>(
      extras['surchargeBorderStyle'],
      SURCHARGE_BORDER_STYLES,
      DEFAULT_BRANDING.surchargeBorderStyle,
    ),
    surchargeWeight: pickEnum<SurchargeWeight>(
      extras['surchargeWeight'],
      SURCHARGE_WEIGHTS,
      DEFAULT_BRANDING.surchargeWeight,
    ),
    surchargeSign: pickEnum<SurchargeSign>(
      extras['surchargeSign'],
      SURCHARGE_SIGNS,
      DEFAULT_BRANDING.surchargeSign,
    ),
    surchargeUppercase: parseBool(
      extras['surchargeUppercase'],
      DEFAULT_BRANDING.surchargeUppercase,
    ),
    surchargeLabel: pickEnum<SurchargeLabelMode>(
      extras['surchargeLabel'],
      SURCHARGE_LABEL_MODES,
      DEFAULT_BRANDING.surchargeLabel,
    ),
    surchargeFigure: pickEnum<SurchargeFigureMode>(
      extras['surchargeFigure'],
      SURCHARGE_FIGURE_MODES,
      DEFAULT_BRANDING.surchargeFigure,
    ),
    discountFigure: pickEnum<SurchargeFigureMode>(
      extras['discountFigure'],
      SURCHARGE_FIGURE_MODES,
      DEFAULT_BRANDING.discountFigure,
    ),
    surchargeWordTranslations: parseTranslations(extras['surchargeWordTranslations']),
    discountWordTranslations: parseTranslations(extras['discountWordTranslations']),
    headerTextTranslations: parseTranslations(extras['headerTextTranslations']),
    payButtonLabelTranslations: parseTranslations(extras['payButtonLabelTranslations']),
    cardTermsMessageTranslations: parseTranslations(extras['cardTermsMessageTranslations']),
    footerTextTranslations: parseTranslations(extras['footerTextTranslations']),
    surchargeColor: s(extras['surchargeColor']) || DEFAULT_BRANDING.surchargeColor,
    discountColor: s(extras['discountColor']) || DEFAULT_BRANDING.discountColor,

    layout: pickEnum<LayoutStyle>(extras['layout'], ['compact', 'split'], DEFAULT_BRANDING.layout),
    summaryPosition: pickEnum<SummaryPosition>(
      extras['summaryPosition'],
      ['left', 'right'],
      DEFAULT_BRANDING.summaryPosition,
    ),
    showOrderSummary: parseBool(extras['showOrderSummary'], DEFAULT_BRANDING.showOrderSummary),
    summaryGradient: parseBool(extras['summaryGradient'], DEFAULT_BRANDING.summaryGradient),
    showTotal: parseBool(extras['showTotal'], DEFAULT_BRANDING.showTotal),
    totalLabel: s(extras['totalLabel']) || DEFAULT_BRANDING.totalLabel,
    showCurrencyCode: parseBool(extras['showCurrencyCode'], DEFAULT_BRANDING.showCurrencyCode),
    showOrderItems: parseBool(extras['showOrderItems'], DEFAULT_BRANDING.showOrderItems),
    cancelLinkEnabled: parseBool(extras['cancelLinkEnabled'], DEFAULT_BRANDING.cancelLinkEnabled),
    cancelLinkPosition: pickEnum<'top' | 'bottom'>(
      extras['cancelLinkPosition'],
      ['top', 'bottom'],
      DEFAULT_BRANDING.cancelLinkPosition,
    ),
    cancelLinkStyle: pickEnum<'link' | 'button'>(
      extras['cancelLinkStyle'],
      ['link', 'button'],
      DEFAULT_BRANDING.cancelLinkStyle,
    ),

    trustBadges: trustRow.trustBadges,
    notices: trustRow.notices,
    customFields: decodedCustomFields ?? [],
    methodSectionOrder: decodeMethodSectionOrder(extras[METHOD_SECTION_ORDER_KEY]),
    methodSectionLabels: decodeMethodSectionLabels(extras[METHOD_SECTION_LABELS_KEY]),

    headerText: s(source.payment_form_header_text),
    payButtonLabel: s(source.payment_button_text),
    cardTermsMessage: s(source.custom_message_for_card_terms),
    footerText: s(extras['footerText']),
    supportEmail: s(extras['supportEmail']),

    paymentLayout: pickEnum<PaymentLayout>(
      source.sdk_layout,
      ['tabs', 'accordion', 'spaced_accordion'],
      DEFAULT_BRANDING.paymentLayout,
    ),
    labelStyle,
    showPoweredBy: source.branding_visibility !== false,

    // The two that can be empty use `pickEnumOrEmpty`, not `pickEnum`: a
    // value this build does not know has to decode to "unset" and defer, the
    // same as a key that was never written. Sending it to a concrete default
    // instead would make an older checkout override the connector account of
    // a merchant who picked a preset a newer dashboard offers.
    airwallexCardPreset: pickEnumOrEmpty<AirwallexCardPreset>(
      extras['airwallexCardPreset'],
      AIRWALLEX_CARD_PRESETS,
    ),
    airwallexHideCard: parseBool(extras['airwallexHideCard'], DEFAULT_BRANDING.airwallexHideCard),
    airwallexHideCardTitle: parseBool(
      extras['airwallexHideCardTitle'],
      DEFAULT_BRANDING.airwallexHideCardTitle,
    ),
    airwallexFieldChrome: pickEnum<AirwallexFieldChrome>(
      extras['airwallexFieldChrome'],
      AIRWALLEX_FIELD_CHROMES,
      DEFAULT_BRANDING.airwallexFieldChrome,
    ),
    airwallexLabelStyle: pickEnumOrEmpty<AirwallexLabelStyle>(
      extras['airwallexLabelStyle'],
      AIRWALLEX_LABEL_STYLES,
    ),
    airwallexFieldFontSize: pickEnum<AirwallexFieldFontSize>(
      extras['airwallexFieldFontSize'],
      AIRWALLEX_FIELD_FONT_SIZES,
      DEFAULT_BRANDING.airwallexFieldFontSize,
    ),
    airwallexFieldWeight: pickEnum<AirwallexFieldWeight>(
      extras['airwallexFieldWeight'],
      AIRWALLEX_FIELD_WEIGHTS,
      DEFAULT_BRANDING.airwallexFieldWeight,
    ),
    airwallexFocusRing: pickEnum<AirwallexFocusRing>(
      extras['airwallexFocusRing'],
      AIRWALLEX_FOCUS_RINGS,
      DEFAULT_BRANDING.airwallexFocusRing,
    ),
    airwallexAutofill: pickEnum<AirwallexAutofillTreatment>(
      extras['airwallexAutofill'],
      AIRWALLEX_AUTOFILL_TREATMENTS,
      DEFAULT_BRANDING.airwallexAutofill,
    ),
    airwallexValidTint: parseBool(
      extras['airwallexValidTint'],
      DEFAULT_BRANDING.airwallexValidTint,
    ),
    airwallexNetworkMarks: parseBool(
      extras['airwallexNetworkMarks'],
      DEFAULT_BRANDING.airwallexNetworkMarks,
    ),

    stripeTheme: pickEnum<StripeTheme>(
      extras['stripeTheme'],
      STRIPE_THEMES,
      DEFAULT_BRANDING.stripeTheme,
    ),
    stripeInputs: pickEnum<StripeInputStyle>(
      extras['stripeInputs'],
      STRIPE_INPUT_STYLES,
      DEFAULT_BRANDING.stripeInputs,
    ),
    stripeLabels: pickEnum<StripeLabelPlacement>(
      extras['stripeLabels'],
      STRIPE_LABEL_PLACEMENTS,
      DEFAULT_BRANDING.stripeLabels,
    ),
    stripeSpacing: pickEnum<StripeSpacing>(
      extras['stripeSpacing'],
      STRIPE_SPACINGS,
      DEFAULT_BRANDING.stripeSpacing,
    ),
    stripeFieldFontSize: pickEnum<CardFieldFontSize>(
      extras['stripeFieldFontSize'],
      CARD_FIELD_FONT_SIZES,
      DEFAULT_BRANDING.stripeFieldFontSize,
    ),
    stripeFieldWeight: pickEnum<CardFieldWeight>(
      extras['stripeFieldWeight'],
      CARD_FIELD_WEIGHTS,
      DEFAULT_BRANDING.stripeFieldWeight,
    ),
    stripeFocusRing: pickEnum<CardFocusRing>(
      extras['stripeFocusRing'],
      CARD_FOCUS_RINGS,
      DEFAULT_BRANDING.stripeFocusRing,
    ),
    stripeDisableAnimations: parseBool(
      extras['stripeDisableAnimations'],
      DEFAULT_BRANDING.stripeDisableAnimations,
    ),
    paypalCardLayout: pickEnum<PaypalCardLayout>(
      extras['paypalCardLayout'],
      PAYPAL_CARD_LAYOUTS,
      DEFAULT_BRANDING.paypalCardLayout,
    ),
    paypalLabelStyle: pickEnum<PaypalLabelStyle>(
      extras['paypalLabelStyle'],
      PAYPAL_LABEL_STYLES,
      DEFAULT_BRANDING.paypalLabelStyle,
    ),
    paypalFieldChrome: pickEnum<CardFieldChrome>(
      extras['paypalFieldChrome'],
      CARD_FIELD_CHROMES,
      DEFAULT_BRANDING.paypalFieldChrome,
    ),
    paypalFieldFontSize: pickEnum<CardFieldFontSize>(
      extras['paypalFieldFontSize'],
      CARD_FIELD_FONT_SIZES,
      DEFAULT_BRANDING.paypalFieldFontSize,
    ),
    paypalFieldWeight: pickEnum<CardFieldWeight>(
      extras['paypalFieldWeight'],
      CARD_FIELD_WEIGHTS,
      DEFAULT_BRANDING.paypalFieldWeight,
    ),
    paypalFocusRing: pickEnum<CardFocusRing>(
      extras['paypalFocusRing'],
      CARD_FOCUS_RINGS,
      DEFAULT_BRANDING.paypalFocusRing,
    ),
    paypalValidTint: parseBool(extras['paypalValidTint'], DEFAULT_BRANDING.paypalValidTint),
    nomupayFormStyle: pickEnum<NomupayFormStyle>(
      extras['nomupayFormStyle'],
      NOMUPAY_FORM_STYLES,
      DEFAULT_BRANDING.nomupayFormStyle,
    ),
    nomupayShowLabels: parseBool(extras['nomupayShowLabels'], DEFAULT_BRANDING.nomupayShowLabels),
    nomupayShowPlaceholders: parseBool(
      extras['nomupayShowPlaceholders'],
      DEFAULT_BRANDING.nomupayShowPlaceholders,
    ),
    nomupayShowCvvHint: parseBool(
      extras['nomupayShowCvvHint'],
      DEFAULT_BRANDING.nomupayShowCvvHint,
    ),
    nomupayFieldChrome: pickEnum<CardFieldChrome>(
      extras['nomupayFieldChrome'],
      CARD_FIELD_CHROMES,
      DEFAULT_BRANDING.nomupayFieldChrome,
    ),
    nomupayFieldFontSize: pickEnum<CardFieldFontSize>(
      extras['nomupayFieldFontSize'],
      CARD_FIELD_FONT_SIZES,
      DEFAULT_BRANDING.nomupayFieldFontSize,
    ),
    nomupayFocusRing: pickEnum<CardFocusRing>(
      extras['nomupayFocusRing'],
      CARD_FOCUS_RINGS,
      DEFAULT_BRANDING.nomupayFocusRing,
    ),
    nomupayApplePayButtonStyle: pickEnum<ApplePayButtonStyle>(
      extras['nomupayApplePayButtonStyle'],
      APPLE_PAY_BUTTON_STYLES,
      DEFAULT_BRANDING.nomupayApplePayButtonStyle,
    ),
    nomupayApplePayButtonType: pickEnum<ApplePayButtonType>(
      extras['nomupayApplePayButtonType'],
      APPLE_PAY_BUTTON_TYPES,
      DEFAULT_BRANDING.nomupayApplePayButtonType,
    ),
    nomupayGooglePayButtonColor: pickEnum<GooglePayButtonColor>(
      extras['nomupayGooglePayButtonColor'],
      GOOGLE_PAY_BUTTON_COLORS,
      DEFAULT_BRANDING.nomupayGooglePayButtonColor,
    ),
    nomupayGooglePayButtonType: pickEnum<GooglePayButtonType>(
      extras['nomupayGooglePayButtonType'],
      GOOGLE_PAY_BUTTON_TYPES,
      DEFAULT_BRANDING.nomupayGooglePayButtonType,
    ),

    customCss: s(extras['customCss']),
  };
}

// Encoded request shape — the union of fields the encoder fills. Compatible
// with `BusinessPaymentLinkConfig` / `PaymentLinkConfigRequest` via
// structural typing on the consumer side.
export interface EncodedBranding {
  theme: string;
  logo: string | null;
  seller_name: string | null;
  sdk_layout: PaymentLayout;
  payment_button_text: string | null;
  payment_button_colour: string;
  payment_button_text_colour: string;
  background_colour: string;
  payment_form_header_text: string | null;
  payment_form_label_type: 'above' | 'never';
  custom_message_for_card_terms: string | null;
  sdk_ui_rules: Record<string, Record<string, string>>;
  branding_visibility: boolean;
}

// Encode a CheckoutBranding into the API request shape. `base` is the
// existing config (if any) -- preserves bag entries the form doesn't surface
// (other sdk_ui_rules groups, business_specific_configs, etc.) by spreading
// it through.
//
// `TBase` is constrained to `object | null | undefined` (not
// `Record<string, unknown>`) so consumers can pass interface-typed values
// directly. TS interfaces don't get an implicit string index signature, so
// they don't structurally satisfy `Record<string, unknown>` even when their
// property values would all unify to `unknown` — `object` admits them
// without forcing a cast at every call site. We only spread the value, so
// no index-signature semantics are needed at runtime.
export function encodeBranding<TBase extends object | null | undefined>(
  branding: CheckoutBranding,
  base?: TBase,
): EncodedBranding & (TBase extends object ? TBase : Record<string, never>) {
  const trim = (v: string): string | null => {
    const t = v.trim();
    return t.length > 0 ? t : null;
  };

  // We only read one property off `base` and structural typing on `object`
  // doesn't permit index access, so go through `unknown` here.
  const existingRules = ((base as unknown as Record<string, unknown> | undefined)?.[
    'sdk_ui_rules'
  ] ?? {}) as Record<string, Record<string, string>>;
  const extras: Record<string, string> = {
    surface: branding.surface,
    text: branding.text,
    heading: branding.heading,
    muted: branding.muted,
    border: branding.border,
    accentText: branding.accentText,
    fontFamily: branding.fontFamily,
    headingWeight: branding.headingWeight,
    radiusSurface: branding.radiusSurface,
    radiusInput: branding.radiusInput,
    radiusButton: branding.radiusButton,
    radiusBadge: branding.radiusBadge,
    surfaceStyle: branding.surfaceStyle,
    surfacePadding: branding.surfacePadding,
    verticalGap: branding.verticalGap,
    inputSize: branding.inputSize,
    buttonSize: branding.buttonSize,
    showSurcharge: String(branding.showSurcharge),
    surchargePosition: branding.surchargePosition,
    nativeElementPosition: branding.nativeElementPosition,
    nativeElementStacked: String(branding.nativeElementStacked),
    surchargeStyle: branding.surchargeStyle,
    surchargeSize: branding.surchargeSize,
    surchargeShape: branding.surchargeShape,
    surchargeBorderWidth: branding.surchargeBorderWidth,
    surchargeBorderStyle: branding.surchargeBorderStyle,
    surchargeWeight: branding.surchargeWeight,
    surchargeSign: branding.surchargeSign,
    surchargeUppercase: String(branding.surchargeUppercase),
    surchargeLabel: branding.surchargeLabel,
    surchargeFigure: branding.surchargeFigure,
    discountFigure: branding.discountFigure,
    surchargeColor: branding.surchargeColor,
    discountColor: branding.discountColor,
    layout: branding.layout,
    summaryPosition: branding.summaryPosition,
    showOrderSummary: String(branding.showOrderSummary),
    summaryGradient: String(branding.summaryGradient),
    showTotal: String(branding.showTotal),
    totalLabel: branding.totalLabel,
    showCurrencyCode: String(branding.showCurrencyCode),
    showOrderItems: String(branding.showOrderItems),
    cancelLinkEnabled: String(branding.cancelLinkEnabled),
    cancelLinkPosition: branding.cancelLinkPosition,
    cancelLinkStyle: branding.cancelLinkStyle,
    showLogo: String(branding.showLogo),
    logoShape: branding.logoShape,
    logoSize: branding.logoSize,
    labelStyle: branding.labelStyle,
    // Written unconditionally, empty string included. The encoder REPLACES
    // `sdk_ui_rules.branding` wholesale, so a key it skips is a key it
    // deletes — and here the empty string is a real value ("no choice made"),
    // not an absence. Omitting it would be indistinguishable from omitting it
    // by accident, which is how `summaryOrder` was lost.
    airwallexCardPreset: branding.airwallexCardPreset,
    airwallexHideCard: String(branding.airwallexHideCard),
    airwallexHideCardTitle: String(branding.airwallexHideCardTitle),
    airwallexFieldChrome: branding.airwallexFieldChrome,
    airwallexLabelStyle: branding.airwallexLabelStyle,
    airwallexFieldFontSize: branding.airwallexFieldFontSize,
    airwallexFieldWeight: branding.airwallexFieldWeight,
    airwallexFocusRing: branding.airwallexFocusRing,
    airwallexAutofill: branding.airwallexAutofill,
    airwallexValidTint: String(branding.airwallexValidTint),
    airwallexNetworkMarks: String(branding.airwallexNetworkMarks),
    stripeTheme: branding.stripeTheme,
    stripeInputs: branding.stripeInputs,
    stripeLabels: branding.stripeLabels,
    stripeSpacing: branding.stripeSpacing,
    stripeFieldFontSize: branding.stripeFieldFontSize,
    stripeFieldWeight: branding.stripeFieldWeight,
    stripeFocusRing: branding.stripeFocusRing,
    stripeDisableAnimations: String(branding.stripeDisableAnimations),
    paypalCardLayout: branding.paypalCardLayout,
    paypalLabelStyle: branding.paypalLabelStyle,
    paypalFieldChrome: branding.paypalFieldChrome,
    paypalFieldFontSize: branding.paypalFieldFontSize,
    paypalFieldWeight: branding.paypalFieldWeight,
    paypalFocusRing: branding.paypalFocusRing,
    paypalValidTint: String(branding.paypalValidTint),
    nomupayFormStyle: branding.nomupayFormStyle,
    nomupayShowLabels: String(branding.nomupayShowLabels),
    nomupayShowPlaceholders: String(branding.nomupayShowPlaceholders),
    nomupayShowCvvHint: String(branding.nomupayShowCvvHint),
    nomupayFieldChrome: branding.nomupayFieldChrome,
    nomupayFieldFontSize: branding.nomupayFieldFontSize,
    nomupayFocusRing: branding.nomupayFocusRing,
    nomupayApplePayButtonStyle: branding.nomupayApplePayButtonStyle,
    nomupayApplePayButtonType: branding.nomupayApplePayButtonType,
    nomupayGooglePayButtonColor: branding.nomupayGooglePayButtonColor,
    nomupayGooglePayButtonType: branding.nomupayGooglePayButtonType,
    // Always written, `[]` included — its presence is what stops a later
    // decode from migrating the shop again (`resolveNoticesAndBadges`).
    notices: encodeNotices(branding.notices),
    [METHOD_SECTION_ORDER_KEY]: encodeMethodSectionOrder(branding.methodSectionOrder),
  };
  // Badges only when there are some. Not for tidiness: this group
  // is rebuilt on every save, so a writer that predates notices drops them.
  // Such a writer decodes a missing `trustBadges` to its stock pair and saves
  // that, which decodes here to the default notices again. Had `[]` been
  // written, it would save `[]` back, and `[]` without notices reads as "the
  // merchant removed the trust row", so the checkout would show nothing.
  if (branding.trustBadges.length > 0) {
    extras['trustBadges'] = encodeBadges(branding.trustBadges);
  }
  // Written only when there is one, so an untouched profile's bag is unchanged.
  const sectionLabels = encodeMethodSectionLabels(branding.methodSectionLabels);
  if (sectionLabels) extras[METHOD_SECTION_LABELS_KEY] = sectionLabels;
  // Omit the key entirely when there are no fields — decode treats a
  // missing entry the same as an empty list, and the bag stays clean for
  // merchants who never touch the feature.
  if (branding.customFields.length > 0) {
    extras['customFields'] = encodeCustomFields(branding.customFields);
  }
  // Localized copy, each written only when the merchant actually set one, so
  // an untouched profile's bag is unchanged by this feature existing.
  for (const key of [
    'surchargeWordTranslations',
    'discountWordTranslations',
    'headerTextTranslations',
    'payButtonLabelTranslations',
    'cardTermsMessageTranslations',
    'footerTextTranslations',
  ] as const) {
    const encoded = encodeTranslations(branding[key]);
    if (encoded) extras[key] = encoded;
  }
  const tagline = trim(branding.tagline);
  if (tagline) extras['tagline'] = tagline;
  const footer = trim(branding.footerText);
  if (footer) extras['footerText'] = footer;
  const support = trim(branding.supportEmail);
  if (support) extras['supportEmail'] = support;
  // Persist raw — sanitization runs at render time so we keep the merchant's
  // input as-typed (preserves comments, whitespace) and let the checkout
  // strip dangerous tokens consistently across all readers.
  const css = trim(branding.customCss);
  if (css) extras['customCss'] = css;

  const nextRules: Record<string, Record<string, string>> = {
    ...existingRules,
    [BRANDING_GROUP_KEY]: extras,
  };

  // Map `hidden` back to the legacy `never` token the API's label type
  // enum understands. `floating` no longer exists.
  const legacyLabel: 'above' | 'never' = branding.labelStyle === 'hidden' ? 'never' : 'above';

  const flatFields: EncodedBranding = {
    theme: branding.primary,
    logo: trim(branding.logoUrl),
    seller_name: trim(branding.displayName),
    sdk_layout: branding.paymentLayout,
    payment_button_text: trim(branding.payButtonLabel),
    payment_button_colour: branding.buttonBackground,
    payment_button_text_colour: branding.buttonText,
    background_colour: branding.background,
    payment_form_header_text: trim(branding.headerText),
    payment_form_label_type: legacyLabel,
    custom_message_for_card_terms: trim(branding.cardTermsMessage),
    sdk_ui_rules: nextRules,
    branding_visibility: branding.showPoweredBy,
  };

  // Spread `base` first to preserve fields the encoder doesn't surface
  // (show_card_terms, show_card_form_by_default, hide_card_nickname_field,
  // enable_button_only_on_form_ready, skip_status_screen,
  // transaction_details, background_image, details_layout,
  // custom_message_for_payment_method_types, payment_link_ui_rules,
  // color_icon_card_cvc_error, is_setup_mandate_flow,
  // enabled_saved_payment_method, display_sdk_only,
  // business_specific_configs, domain_name, allowed_domains).
  return {
    ...base,
    ...flatFields,
  } as EncodedBranding & (TBase extends object ? TBase : Record<string, never>);
}

// --- Import / Export envelope ------------------------------------------

// Versioned envelope used by the merchant control-center's "Import /
// Export" buttons. Bumping `version` is informational; the importer is
// graceful and accepts any version (or no envelope at all).
export const BRANDING_EXPORT_FORMAT = 'delopay-checkout-branding';
export const BRANDING_EXPORT_VERSION = 1;

export interface BrandingExport {
  format: typeof BRANDING_EXPORT_FORMAT;
  version: number;
  exported_at: string;
  branding: CheckoutBranding;
}

export function buildBrandingExport(branding: CheckoutBranding): BrandingExport {
  return {
    format: BRANDING_EXPORT_FORMAT,
    version: BRANDING_EXPORT_VERSION,
    exported_at: new Date().toISOString(),
    branding: cloneBranding(branding),
  };
}

// Resolve arbitrary JSON into a fully-typed CheckoutBranding. Designed to
// never throw — every field is independently validated against its type
// and silently falls back to the light-mode default if missing or
// malformed:
//   - extra fields the form doesn't know about are ignored,
//   - missing fields inherit defaults,
//   - hex colors that fail the regex stay at the default,
//   - enums outside the allowed set fall back,
//   - a payload with no recognizable shape produces DEFAULT_BRANDING.
//
// Accepts either a bare CheckoutBranding object or an envelope produced by
// `buildBrandingExport()`. The caller (the import handler) only needs to
// catch JSON.parse errors; everything past that point is total.
export function parseImportedBranding(raw: unknown): CheckoutBranding {
  // Unwrap the envelope if present. Don't gate on version — bumping it is
  // informational; this function is the migration layer.
  const root: Record<string, unknown> | null =
    isObject(raw) && isObject(raw['branding'])
      ? (raw['branding'] as Record<string, unknown>)
      : isObject(raw)
        ? raw
        : null;

  if (!root) return cloneBranding(DEFAULT_BRANDING);

  const dflt = DEFAULT_BRANDING;
  const sStr = (v: unknown, fallback: string): string => (typeof v === 'string' ? v : fallback);
  const sHex = (v: unknown, fallback: string): string =>
    typeof v === 'string' && isHexColor(v) ? v : fallback;

  // The same rule as `decodeBranding`: an export made before notices existed
  // carries badges only, and is migrated exactly as a saved shop would be.
  const { trustBadges, notices } = resolveNoticesAndBadges(
    parseTrustBadgesLoose(root['trustBadges']),
    parseNoticesLoose(root['notices']),
  );
  const customFields = parseCustomFieldsLoose(root['customFields']) ?? [];

  // `floating` from older exports normalizes to `above`; see decodeBranding.
  const labelStyle: LabelStyle = (() => {
    const v = root['labelStyle'];
    if (v === 'above' || v === 'hidden') return v;
    return dflt.labelStyle;
  })();

  return {
    displayName: sStr(root['displayName'], dflt.displayName),
    logoUrl: sStr(root['logoUrl'], dflt.logoUrl),
    tagline: sStr(root['tagline'], dflt.tagline),
    showLogo: parseBool(root['showLogo'], dflt.showLogo),
    logoShape: pickEnum<LogoShape>(
      root['logoShape'],
      ['square', 'rounded', 'circle'],
      dflt.logoShape,
    ),
    logoSize: pickEnum<LogoSize>(root['logoSize'], ['sm', 'md', 'lg'], dflt.logoSize),

    primary: sHex(root['primary'], dflt.primary),
    background: sHex(root['background'], dflt.background),
    surface: sHex(root['surface'], dflt.surface),
    text: sHex(root['text'], dflt.text),
    heading: sHex(root['heading'], dflt.heading),
    muted: sHex(root['muted'], dflt.muted),
    border: sHex(root['border'], dflt.border),
    accentText: sHex(root['accentText'], dflt.accentText),
    buttonBackground: sHex(root['buttonBackground'], dflt.buttonBackground),
    buttonText: sHex(root['buttonText'], dflt.buttonText),

    fontFamily: pickEnum<FontFamily>(root['fontFamily'], ALL_FONT_FAMILIES, dflt.fontFamily),
    headingWeight: pickEnum<FontWeight>(
      root['headingWeight'],
      ['regular', 'medium', 'semibold', 'bold'],
      dflt.headingWeight,
    ),

    radiusSurface: pickEnum<NonPillRadius>(
      root['radiusSurface'],
      NON_PILL_RADII,
      dflt.radiusSurface,
    ),
    radiusInput: pickEnum<NonPillRadius>(root['radiusInput'], NON_PILL_RADII, dflt.radiusInput),
    radiusButton: pickEnum<CornerRadius>(root['radiusButton'], ALL_RADII, dflt.radiusButton),
    radiusBadge: pickEnum<CornerRadius>(root['radiusBadge'], ALL_RADII, dflt.radiusBadge),
    surfaceStyle: pickEnum<SurfaceStyle>(
      root['surfaceStyle'],
      ['flat', 'outlined', 'elevated'],
      dflt.surfaceStyle,
    ),
    surfacePadding: pickEnum<SpacingScale>(
      root['surfacePadding'],
      ['compact', 'comfortable', 'spacious'],
      dflt.surfacePadding,
    ),
    verticalGap: pickEnum<SpacingScale>(
      root['verticalGap'],
      ['compact', 'comfortable', 'spacious'],
      dflt.verticalGap,
    ),
    inputSize: pickEnum<SizeScale>(root['inputSize'], ['sm', 'md', 'lg'], dflt.inputSize),
    buttonSize: pickEnum<SizeScale>(root['buttonSize'], ['sm', 'md', 'lg'], dflt.buttonSize),

    showSurcharge: parseBool(root['showSurcharge'], dflt.showSurcharge),
    surchargePosition: pickEnum<SurchargePosition>(
      root['surchargePosition'],
      SURCHARGE_POSITIONS,
      dflt.surchargePosition,
    ),
    nativeElementStacked: parseBool(root['nativeElementStacked'], dflt.nativeElementStacked),
    nativeElementPosition: pickEnum<NativeElementPosition>(
      root['nativeElementPosition'],
      NATIVE_ELEMENT_POSITIONS,
      dflt.nativeElementPosition,
    ),
    surchargeStyle: pickEnum<SurchargeStyle>(
      root['surchargeStyle'],
      SURCHARGE_STYLES,
      dflt.surchargeStyle,
    ),
    surchargeSize: pickEnum<SurchargeSize>(
      root['surchargeSize'],
      SURCHARGE_SIZES,
      dflt.surchargeSize,
    ),
    surchargeShape: pickEnum<SurchargeShape>(
      root['surchargeShape'],
      SURCHARGE_SHAPES,
      dflt.surchargeShape,
    ),
    surchargeBorderWidth: pickEnum<SurchargeBorderWidth>(
      root['surchargeBorderWidth'],
      SURCHARGE_BORDER_WIDTHS,
      dflt.surchargeBorderWidth,
    ),
    surchargeBorderStyle: pickEnum<SurchargeBorderStyle>(
      root['surchargeBorderStyle'],
      SURCHARGE_BORDER_STYLES,
      dflt.surchargeBorderStyle,
    ),
    surchargeWeight: pickEnum<SurchargeWeight>(
      root['surchargeWeight'],
      SURCHARGE_WEIGHTS,
      dflt.surchargeWeight,
    ),
    surchargeSign: pickEnum<SurchargeSign>(
      root['surchargeSign'],
      SURCHARGE_SIGNS,
      dflt.surchargeSign,
    ),
    surchargeUppercase: parseBool(root['surchargeUppercase'], dflt.surchargeUppercase),
    surchargeLabel: pickEnum<SurchargeLabelMode>(
      root['surchargeLabel'],
      SURCHARGE_LABEL_MODES,
      dflt.surchargeLabel,
    ),
    surchargeFigure: pickEnum<SurchargeFigureMode>(
      root['surchargeFigure'],
      SURCHARGE_FIGURE_MODES,
      dflt.surchargeFigure,
    ),
    discountFigure: pickEnum<SurchargeFigureMode>(
      root['discountFigure'],
      SURCHARGE_FIGURE_MODES,
      dflt.discountFigure,
    ),
    surchargeWordTranslations: parseTranslations(root['surchargeWordTranslations']),
    discountWordTranslations: parseTranslations(root['discountWordTranslations']),
    headerTextTranslations: parseTranslations(root['headerTextTranslations']),
    payButtonLabelTranslations: parseTranslations(root['payButtonLabelTranslations']),
    cardTermsMessageTranslations: parseTranslations(root['cardTermsMessageTranslations']),
    footerTextTranslations: parseTranslations(root['footerTextTranslations']),
    surchargeColor: sHex(root['surchargeColor'], dflt.surchargeColor),
    discountColor: sHex(root['discountColor'], dflt.discountColor),

    layout: pickEnum<LayoutStyle>(root['layout'], ['compact', 'split'], dflt.layout),
    summaryPosition: pickEnum<SummaryPosition>(
      root['summaryPosition'],
      ['left', 'right'],
      dflt.summaryPosition,
    ),
    showOrderSummary: parseBool(root['showOrderSummary'], dflt.showOrderSummary),
    summaryGradient: parseBool(root['summaryGradient'], dflt.summaryGradient),
    showTotal: parseBool(root['showTotal'], dflt.showTotal),
    totalLabel: sStr(root['totalLabel'], dflt.totalLabel),
    showCurrencyCode: parseBool(root['showCurrencyCode'], dflt.showCurrencyCode),
    showOrderItems: parseBool(root['showOrderItems'], dflt.showOrderItems),

    trustBadges,
    notices,
    customFields,
    cancelLinkEnabled: parseBool(root['cancelLinkEnabled'], dflt.cancelLinkEnabled),
    cancelLinkPosition: pickEnum<'top' | 'bottom'>(
      root['cancelLinkPosition'],
      ['top', 'bottom'],
      dflt.cancelLinkPosition,
    ),
    cancelLinkStyle: pickEnum<'link' | 'button'>(
      root['cancelLinkStyle'],
      ['link', 'button'],
      dflt.cancelLinkStyle,
    ),
    // An export writes the decoded maps; a hand-written file may carry the
    // JSON strings the bag uses. Both are accepted, and neither is required.
    methodSectionOrder: parseSectionOrderLoose(root[METHOD_SECTION_ORDER_KEY]),
    methodSectionLabels: parseSectionLabelsLoose(root[METHOD_SECTION_LABELS_KEY]),

    headerText: sStr(root['headerText'], dflt.headerText),
    payButtonLabel: sStr(root['payButtonLabel'], dflt.payButtonLabel),
    cardTermsMessage: sStr(root['cardTermsMessage'], dflt.cardTermsMessage),
    footerText: sStr(root['footerText'], dflt.footerText),
    supportEmail: sStr(root['supportEmail'], dflt.supportEmail),

    paymentLayout: pickEnum<PaymentLayout>(
      root['paymentLayout'],
      ['tabs', 'accordion', 'spaced_accordion'],
      dflt.paymentLayout,
    ),
    labelStyle,
    showPoweredBy: parseBool(root['showPoweredBy'], dflt.showPoweredBy),

    // An imported profile that predates this section carries none of these
    // and lands on the defaults — which is to say on "defer", so importing an
    // old export into a shop does not take that shop's connector setting
    // away. The one field that would be dangerous to import loosely is the
    // hide switch, and `parseBool` only says true for a literal `true`.
    airwallexCardPreset: pickEnumOrEmpty<AirwallexCardPreset>(
      root['airwallexCardPreset'],
      AIRWALLEX_CARD_PRESETS,
    ),
    airwallexHideCard: parseBool(root['airwallexHideCard'], dflt.airwallexHideCard),
    airwallexHideCardTitle: parseBool(root['airwallexHideCardTitle'], dflt.airwallexHideCardTitle),
    airwallexFieldChrome: pickEnum<AirwallexFieldChrome>(
      root['airwallexFieldChrome'],
      AIRWALLEX_FIELD_CHROMES,
      dflt.airwallexFieldChrome,
    ),
    airwallexLabelStyle: pickEnumOrEmpty<AirwallexLabelStyle>(
      root['airwallexLabelStyle'],
      AIRWALLEX_LABEL_STYLES,
    ),
    airwallexFieldFontSize: pickEnum<AirwallexFieldFontSize>(
      root['airwallexFieldFontSize'],
      AIRWALLEX_FIELD_FONT_SIZES,
      dflt.airwallexFieldFontSize,
    ),
    airwallexFieldWeight: pickEnum<AirwallexFieldWeight>(
      root['airwallexFieldWeight'],
      AIRWALLEX_FIELD_WEIGHTS,
      dflt.airwallexFieldWeight,
    ),
    airwallexFocusRing: pickEnum<AirwallexFocusRing>(
      root['airwallexFocusRing'],
      AIRWALLEX_FOCUS_RINGS,
      dflt.airwallexFocusRing,
    ),
    airwallexAutofill: pickEnum<AirwallexAutofillTreatment>(
      root['airwallexAutofill'],
      AIRWALLEX_AUTOFILL_TREATMENTS,
      dflt.airwallexAutofill,
    ),
    airwallexValidTint: parseBool(root['airwallexValidTint'], dflt.airwallexValidTint),
    airwallexNetworkMarks: parseBool(root['airwallexNetworkMarks'], dflt.airwallexNetworkMarks),

    stripeTheme: pickEnum<StripeTheme>(root['stripeTheme'], STRIPE_THEMES, dflt.stripeTheme),
    stripeInputs: pickEnum<StripeInputStyle>(
      root['stripeInputs'],
      STRIPE_INPUT_STYLES,
      dflt.stripeInputs,
    ),
    stripeLabels: pickEnum<StripeLabelPlacement>(
      root['stripeLabels'],
      STRIPE_LABEL_PLACEMENTS,
      dflt.stripeLabels,
    ),
    stripeSpacing: pickEnum<StripeSpacing>(
      root['stripeSpacing'],
      STRIPE_SPACINGS,
      dflt.stripeSpacing,
    ),
    stripeFieldFontSize: pickEnum<CardFieldFontSize>(
      root['stripeFieldFontSize'],
      CARD_FIELD_FONT_SIZES,
      dflt.stripeFieldFontSize,
    ),
    stripeFieldWeight: pickEnum<CardFieldWeight>(
      root['stripeFieldWeight'],
      CARD_FIELD_WEIGHTS,
      dflt.stripeFieldWeight,
    ),
    stripeFocusRing: pickEnum<CardFocusRing>(
      root['stripeFocusRing'],
      CARD_FOCUS_RINGS,
      dflt.stripeFocusRing,
    ),
    stripeDisableAnimations: parseBool(
      root['stripeDisableAnimations'],
      dflt.stripeDisableAnimations,
    ),
    paypalCardLayout: pickEnum<PaypalCardLayout>(
      root['paypalCardLayout'],
      PAYPAL_CARD_LAYOUTS,
      dflt.paypalCardLayout,
    ),
    paypalLabelStyle: pickEnum<PaypalLabelStyle>(
      root['paypalLabelStyle'],
      PAYPAL_LABEL_STYLES,
      dflt.paypalLabelStyle,
    ),
    paypalFieldChrome: pickEnum<CardFieldChrome>(
      root['paypalFieldChrome'],
      CARD_FIELD_CHROMES,
      dflt.paypalFieldChrome,
    ),
    paypalFieldFontSize: pickEnum<CardFieldFontSize>(
      root['paypalFieldFontSize'],
      CARD_FIELD_FONT_SIZES,
      dflt.paypalFieldFontSize,
    ),
    paypalFieldWeight: pickEnum<CardFieldWeight>(
      root['paypalFieldWeight'],
      CARD_FIELD_WEIGHTS,
      dflt.paypalFieldWeight,
    ),
    paypalFocusRing: pickEnum<CardFocusRing>(
      root['paypalFocusRing'],
      CARD_FOCUS_RINGS,
      dflt.paypalFocusRing,
    ),
    paypalValidTint: parseBool(root['paypalValidTint'], dflt.paypalValidTint),
    nomupayFormStyle: pickEnum<NomupayFormStyle>(
      root['nomupayFormStyle'],
      NOMUPAY_FORM_STYLES,
      dflt.nomupayFormStyle,
    ),
    nomupayShowLabels: parseBool(root['nomupayShowLabels'], dflt.nomupayShowLabels),
    nomupayShowPlaceholders: parseBool(
      root['nomupayShowPlaceholders'],
      dflt.nomupayShowPlaceholders,
    ),
    nomupayShowCvvHint: parseBool(root['nomupayShowCvvHint'], dflt.nomupayShowCvvHint),
    nomupayFieldChrome: pickEnum<CardFieldChrome>(
      root['nomupayFieldChrome'],
      CARD_FIELD_CHROMES,
      dflt.nomupayFieldChrome,
    ),
    nomupayFieldFontSize: pickEnum<CardFieldFontSize>(
      root['nomupayFieldFontSize'],
      CARD_FIELD_FONT_SIZES,
      dflt.nomupayFieldFontSize,
    ),
    nomupayFocusRing: pickEnum<CardFocusRing>(
      root['nomupayFocusRing'],
      CARD_FOCUS_RINGS,
      dflt.nomupayFocusRing,
    ),
    nomupayApplePayButtonStyle: pickEnum<ApplePayButtonStyle>(
      root['nomupayApplePayButtonStyle'],
      APPLE_PAY_BUTTON_STYLES,
      dflt.nomupayApplePayButtonStyle,
    ),
    nomupayApplePayButtonType: pickEnum<ApplePayButtonType>(
      root['nomupayApplePayButtonType'],
      APPLE_PAY_BUTTON_TYPES,
      dflt.nomupayApplePayButtonType,
    ),
    nomupayGooglePayButtonColor: pickEnum<GooglePayButtonColor>(
      root['nomupayGooglePayButtonColor'],
      GOOGLE_PAY_BUTTON_COLORS,
      dflt.nomupayGooglePayButtonColor,
    ),
    nomupayGooglePayButtonType: pickEnum<GooglePayButtonType>(
      root['nomupayGooglePayButtonType'],
      GOOGLE_PAY_BUTTON_TYPES,
      dflt.nomupayGooglePayButtonType,
    ),

    customCss: sStr(root['customCss'], dflt.customCss).slice(0, CUSTOM_CSS_MAX_LENGTH),
  };
}

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

// Loose trust-badge parser used by the importer. Unlike `decodeBadges`
// (which reads the JSON-stringified API bag), this accepts an array of
// objects directly. Returns null when the input isn't an array — the
// caller falls back to the default badge set; an explicit empty array is
// honored.
function parseTrustBadgesLoose(raw: unknown): TrustBadge[] | null {
  if (!Array.isArray(raw)) return null;
  return raw
    .filter(isObject)
    .map((b, i) => ({
      id: typeof b['id'] === 'string' && b['id'] ? (b['id'] as string) : `badge-${i}`,
      label: typeof b['label'] === 'string' ? (b['label'] as string) : '',
      textColor:
        typeof b['textColor'] === 'string' && isHexColor(b['textColor'] as string)
          ? (b['textColor'] as string)
          : '#0f172a',
      backgroundColor:
        typeof b['backgroundColor'] === 'string' && isHexColor(b['backgroundColor'] as string)
          ? (b['backgroundColor'] as string)
          : '#f1f5f9',
      borderColor:
        typeof b['borderColor'] === 'string' && isHexColor(b['borderColor'] as string)
          ? (b['borderColor'] as string)
          : null,
    }))
    .filter((b) => b.label.length > 0);
}

// --- DOM helpers --------------------------------------------------------

// Write branding tokens onto a target element as CSS custom properties.
// The buyer-facing checkout sets these on `<html>` so every component sees
// the same set, including Stripe Elements (indirectly via
// appearance.variables). The merchant control-center's preview component
// uses an analogous `--p-*` set with `var(--dp-*, fallback)` indirection so
// a custom-CSS rule like `:root{--dp-primary:red}` overrides the preview
// without re-typing the whole brand.
export function applyBrandingVariables(el: HTMLElement, b: CheckoutBranding): void {
  const set = (k: string, v: string) => {
    el.style.setProperty(k, v);
  };

  set('--dp-primary', b.primary);
  set('--dp-bg', b.background);
  set('--dp-surface', b.surface);
  set('--dp-text', b.text);
  set('--dp-heading', b.heading);
  set('--dp-muted', b.muted);
  set('--dp-border', b.border);
  set('--dp-accent-text', b.accentText);
  set('--dp-btn-bg', b.buttonBackground);
  set('--dp-btn-fg', b.buttonText);

  set('--dp-radius-surface', radiusValue(b.radiusSurface));
  set('--dp-radius-input', radiusValue(b.radiusInput));
  set('--dp-radius-button', radiusValue(b.radiusButton));
  set('--dp-radius-badge', radiusValue(b.radiusBadge));

  set('--dp-font', fontStack(b.fontFamily));
  set('--dp-heading-weight', fontWeightValue(b.headingWeight));

  set('--dp-pad-surface', SURFACE_PAD[b.surfacePadding]);
  set('--dp-gap-vertical', VERTICAL_GAP[b.verticalGap]);
  set('--dp-input-pad', INPUT_PAD[b.inputSize]);
  set('--dp-button-pad', BUTTON_PAD[b.buttonSize]);

  // Published whatever `showSurcharge` says: the flag decides whether a figure
  // renders, not what colour it would be, and a stylesheet that reads the
  // variable must not get an empty string when the merchant has one configured.
  set('--dp-surcharge', b.surchargeColor);
  set('--dp-discount', b.discountColor);
  // What `solid` writes ON the fill. Computed here rather than in a stylesheet
  // because CSS cannot ask a colour how light it is, and picking one fixed ink
  // would make either a pale amber or a deep emerald unreadable.
  set('--dp-surcharge-ink', readableInkOn(b.surchargeColor));
  set('--dp-discount-ink', readableInkOn(b.discountColor));
  set('--dp-surcharge-size', SURCHARGE_FONT_SIZE[b.surchargeSize]);
  set('--dp-surcharge-pad', SURCHARGE_PAD[b.surchargeSize]);
  set('--dp-surcharge-radius', SURCHARGE_RADIUS[b.surchargeShape]);
  set('--dp-surcharge-weight', SURCHARGE_FONT_WEIGHT[b.surchargeWeight]);
  // `outline` draws nothing BUT its border, so `none` there would erase the
  // style rather than restyle it — it reads as `thin` instead.
  set(
    '--dp-surcharge-border-width',
    SURCHARGE_BORDER_WIDTH[
      b.surchargeStyle === 'outline' && b.surchargeBorderWidth === 'none'
        ? 'thin'
        : b.surchargeBorderWidth
    ],
  );
  set('--dp-surcharge-border-style', b.surchargeBorderStyle);

  set('--dp-shadow', shadowFor(b.surfaceStyle));
  set(
    '--dp-surface-border',
    b.surfaceStyle === 'outlined' ? `1px solid ${b.border}` : '1px solid transparent',
  );
}

export function shadowFor(style: SurfaceStyle): string {
  switch (style) {
    case 'elevated':
      return '0 1px 2px rgba(15, 23, 42, 0.04), 0 8px 32px -12px rgba(15, 23, 42, 0.08)';
    case 'outlined':
    case 'flat':
    default:
      return 'none';
  }
}
